import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Modal, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import { useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS, useAnimatedStyle } from 'react-native-reanimated';
import { useTheme, type ThemeColors } from '../../utils/theme';
import TagLegend from '../../components/common/TagLegend';
import { useRemindersStore } from '../../stores/reminderStore';
import { useAuthStore } from '../../stores/authStore';
import { useCalendarStore } from '../../stores/calendarStore';
import { useNavigationStore } from '../../stores/navigationStore';
import { useBackHandler } from '../../navigation/useBackHandler';
import { getLocale } from '../../utils/localization';
import { getPrimaryTagColor } from '../../utils/tagColors';
import DayNavigatorSheet from '../../components/calendar/DayNavigatorSheet';
import type { CalendarEvent, Reminder } from '../../types/api';

type TabIcon = React.ComponentProps<typeof Ionicons>['name'];
type StatusFilter = 'overdue' | 'active' | 'recurring' | 'paused' | 'done' | 'google';
type ViewMode = 'month' | 'timeline' | 'week' | 'agenda';

export type CalendarMonthV2FocusRequest = {
  id: number;
  dateKey: string;
  viewMode?: ViewMode;
  openDayPanel?: boolean;
  animateMonthChange?: boolean;
};

type CalendarMonthV2ScreenProps = {
  additionalCalendarEvents?: CalendarEvent[];
  onQuickAdd?: (dateKey?: string) => void;
  onResizeEvent?: (seq: number, durationMinutes: number) => void | Promise<void>;
  onMoveEvent?: (seq: number, newRunAtUtc: string) => void | Promise<void>;
  onChangeColumn?: (updates: Array<{ seq: number; colPref: number }>) => void | Promise<void>;
  onDragCreate?: (startMinutes: number, durationMinutes: number) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  showPrototypeNav?: boolean;
  reservedBottomSpace?: number;
  onPrototypeNavRoutePress?: (route: string) => void;
  focusRequest?: CalendarMonthV2FocusRequest | null;
};

type DenseEvent = {
  id: string;
  body: string;
  dateKey: string;
  minutes: number;
  timeLabel: string;
  color: string;
  tags: string[];
  status: StatusFilter;
  isDone: boolean;
  isPaused: boolean;
  // V10: synced to Google (blue G). isImported = came FROM Google (red G).
  isSynced?: boolean;
  isImported?: boolean;
};

// Google brand blue — synced TO Google. Red — imported FROM Google.
// Never red for status (overdue/today already use reds — the G is distinct
// because it's the recognizable Google glyph, not a pill color).

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TODAY_ACCENT = '#EA4335';
const TODAY_ACCENT_DARK = '#A52714';
const OVERDUE_ACCENT = '#FF3B30';
const DAY_CELL_DOUBLE_TAP_MS = 220;
const MONTH_SWIPE_ACTIVATION_DISTANCE = 10;
const MONTH_SWIPE_DISTANCE = 28;
const MONTH_SWIPE_VELOCITY = 320;
const CELL_COLLAPSE_SWIPE_DISTANCE = 16;
const CELL_COLLAPSE_SWIPE_VELOCITY = 180;
const MONTH_SWAP_DURATION = 220;
const DAY_PANEL_ANIMATION_DURATION = 220;
const YEAR_PICKER_RADIUS = 16;
const YEAR_PICKER_ITEM_HEIGHT = 44;
const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
type LuxonDateTime = ReturnType<typeof DateTime.local>;
type GridPosition = {
  weekIndex: number;
  dayIndex: number;
};
type PrototypePalette = {
  pageBackground: string;
  heroBackground: string;
  heroBorder: string;
  heroPrimaryOrb: string;
  heroSecondaryOrb: string;
  heroButtonBg: string;
  heroButtonBorder: string;
  heroSubtext: string;
  handle: string;
  monthTitle: string;
  weekday: string;
  filterBg: string;
  filterBgActive: string;
  filterBorder: string;
  filterBorderStrong: string;
  dayCellSurface: string;
  dayCellSurfaceRaised: string;
  dayCellBorder: string;
  dayCellBorderStrong: string;
  selectedSurface: string;
  selectedBorder: string;
  todaySurface: string;
  todayBorder: string;
  todaySelectedSurface: string;
  todaySelectedBorder: string;
  todayExpandedSurface: string;
  todayNumberBg: string;
  todayNumberText: string;
  selectedText: string;
  countBadgeBg: string;
  countBadgeBorder: string;
  countBadgeSelectedBg: string;
  countBadgeSelectedBorder: string;
  countBadgeTodayBg: string;
  countBadgeTodayBorder: string;
  countBadgeTodaySelectedBg: string;
  countBadgeTodaySelectedBorder: string;
  countText: string;
  countTextSelected: string;
  countTextToday: string;
  countTextTodaySelected: string;
  densityTint: string;
  eventFallback: string;
  eventText: string;
  eventTextDone: string;
  moreText: string;
  eventDoneBg: string;
  eventDoneBorder: string;
  navBackground: string;
  navBorder: string;
  todayButtonBg: string;
  todayButtonBorder: string;
  navHandle: string;
  dayPanelBackground: string;
  dayPanelBorder: string;
  sheetShadow: string;
};

const PROTOTYPE_LIGHT: PrototypePalette = {
  pageBackground: '#EEF4FA',
  heroBackground: '#2C6297',
  heroBorder: 'rgba(255, 255, 255, 0.18)',
  heroPrimaryOrb: 'rgba(131, 191, 255, 0.24)',
  heroSecondaryOrb: 'rgba(234, 67, 53, 0.14)',
  heroButtonBg: 'rgba(255,255,255,0.14)',
  heroButtonBorder: 'rgba(255,255,255,0.22)',
  heroSubtext: 'rgba(244, 248, 252, 0.76)',
  handle: 'rgba(239, 245, 250, 0.58)',
  monthTitle: '#FFFFFF',
  weekday: '#8EA2B8',
  filterBg: 'rgba(255,255,255,0.10)',
  filterBgActive: 'rgba(255,255,255,0.18)',
  filterBorder: 'rgba(255,255,255,0.16)',
  filterBorderStrong: 'rgba(255,255,255,0.30)',
  dayCellSurface: '#F8FBFE',
  dayCellSurfaceRaised: '#EFF5FB',
  dayCellBorder: 'rgba(64, 109, 154, 0.10)',
  dayCellBorderStrong: 'rgba(64, 109, 154, 0.18)',
  selectedSurface: 'rgba(66, 165, 245, 0.12)',
  selectedBorder: 'rgba(66, 165, 245, 0.30)',
  todaySurface: 'rgba(234, 67, 53, 0.10)',
  todayBorder: 'rgba(234, 67, 53, 0.22)',
  todaySelectedSurface: 'rgba(255, 245, 241, 0.98)',
  todaySelectedBorder: 'rgba(234, 67, 53, 0.30)',
  todayExpandedSurface: '#FFF7F3',
  todayNumberBg: 'rgba(234, 67, 53, 0.16)',
  todayNumberText: '#C24E40',
  selectedText: '#2A69A4',
  countBadgeBg: 'rgba(23, 52, 84, 0.05)',
  countBadgeBorder: 'rgba(64, 109, 154, 0.10)',
  countBadgeSelectedBg: 'rgba(66, 165, 245, 0.12)',
  countBadgeSelectedBorder: 'rgba(66, 165, 245, 0.20)',
  countBadgeTodayBg: 'rgba(234, 67, 53, 0.12)',
  countBadgeTodayBorder: 'rgba(234, 67, 53, 0.18)',
  countBadgeTodaySelectedBg: 'rgba(234, 67, 53, 0.14)',
  countBadgeTodaySelectedBorder: 'rgba(234, 67, 53, 0.22)',
  countText: '#70869C',
  countTextSelected: '#2A69A4',
  countTextToday: '#C24E40',
  countTextTodaySelected: '#C24E40',
  densityTint: '#436A92',
  eventFallback: '#EEF5FB',
  eventText: '#22374C',
  eventTextDone: '#7C8FA4',
  moreText: '#6D849C',
  eventDoneBg: 'rgba(142, 142, 147, 0.11)',
  eventDoneBorder: 'rgba(142, 142, 147, 0.22)',
  navBackground: '#F6FAFD',
  navBorder: 'rgba(64, 109, 154, 0.12)',
  todayButtonBg: 'rgba(66, 165, 245, 0.10)',
  todayButtonBorder: 'rgba(66, 165, 245, 0.22)',
  navHandle: 'rgba(94, 129, 164, 0.24)',
  dayPanelBackground: '#F7FAFD',
  dayPanelBorder: 'rgba(64, 109, 154, 0.16)',
  sheetShadow: '#0C1B2D',
} as const;

const PROTOTYPE_DARK = {
  pageBackground: '#081522',
  heroBackground: '#13345E',
  heroBorder: 'rgba(132, 169, 214, 0.16)',
  heroPrimaryOrb: 'rgba(74, 144, 255, 0.28)',
  heroSecondaryOrb: 'rgba(234, 67, 53, 0.16)',
  heroButtonBg: 'rgba(255,255,255,0.10)',
  heroButtonBorder: 'rgba(255,255,255,0.16)',
  heroSubtext: 'rgba(232, 240, 248, 0.74)',
  handle: 'rgba(191, 206, 226, 0.42)',
  monthTitle: '#F6FAFF',
  weekday: '#7F9BB8',
  filterBg: 'rgba(255,255,255,0.08)',
  filterBgActive: 'rgba(255,255,255,0.16)',
  filterBorder: 'rgba(255,255,255,0.14)',
  filterBorderStrong: 'rgba(255,255,255,0.28)',
  dayCellSurface: '#112338',
  dayCellSurfaceRaised: '#17314A',
  dayCellBorder: 'rgba(132, 169, 214, 0.14)',
  dayCellBorderStrong: 'rgba(132, 169, 214, 0.26)',
  selectedSurface: 'rgba(66, 165, 245, 0.18)',
  selectedBorder: 'rgba(102, 181, 255, 0.56)',
  todaySurface: 'rgba(234, 67, 53, 0.14)',
  todayBorder: 'rgba(255, 132, 118, 0.38)',
  todaySelectedSurface: 'rgba(234, 67, 53, 0.18)',
  todaySelectedBorder: 'rgba(255, 132, 118, 0.54)',
  todayExpandedSurface: '#28181F',
  todayNumberBg: 'rgba(234, 67, 53, 0.22)',
  todayNumberText: '#FFB9AF',
  selectedText: '#B6DAFF',
  countBadgeBg: 'rgba(255,255,255,0.08)',
  countBadgeBorder: 'rgba(173, 197, 221, 0.12)',
  countBadgeSelectedBg: 'rgba(66, 165, 245, 0.22)',
  countBadgeSelectedBorder: 'rgba(102, 181, 255, 0.30)',
  countBadgeTodayBg: 'rgba(234, 67, 53, 0.18)',
  countBadgeTodayBorder: 'rgba(234, 67, 53, 0.30)',
  countBadgeTodaySelectedBg: 'rgba(234, 67, 53, 0.24)',
  countBadgeTodaySelectedBorder: 'rgba(255, 132, 118, 0.34)',
  countText: '#9FB5CC',
  countTextSelected: '#D4E7FA',
  countTextToday: '#FFC0B8',
  countTextTodaySelected: '#FFD3CC',
  densityTint: '#D8E4F0',
  eventFallback: '#17314A',
  eventText: '#E7EEF7',
  eventTextDone: '#91A6BD',
  moreText: '#9FB5CC',
  eventDoneBg: 'rgba(142, 142, 147, 0.16)',
  eventDoneBorder: 'rgba(142, 142, 147, 0.28)',
  navBackground: '#10253C',
  navBorder: 'rgba(132, 169, 214, 0.16)',
  todayButtonBg: 'rgba(66, 165, 245, 0.16)',
  todayButtonBorder: 'rgba(102, 181, 255, 0.32)',
  navHandle: 'rgba(120, 159, 203, 0.36)',
  dayPanelBackground: '#0F2238',
  dayPanelBorder: 'rgba(132, 169, 214, 0.24)',
  sheetShadow: '#020A12',
} as const;
const STATUS_FILTERS: Array<{ key: StatusFilter; label: string; translationKey: string; color: string; icon: TabIcon }> = [
  { key: 'overdue', label: 'Overdue', translationKey: 'reminders.overdue', color: OVERDUE_ACCENT, icon: 'alert-circle-outline' },
  { key: 'active', label: 'Active', translationKey: 'calendar.active', color: '#34C759', icon: 'ellipse' },
  { key: 'recurring', label: 'Repeat', translationKey: 'calendar.recurring', color: '#3478F6', icon: 'repeat-outline' },
  { key: 'google', label: 'Google', translationKey: 'calendar.google', color: '#4285F4', icon: 'logo-google' },
  { key: 'paused', label: 'Paused', translationKey: 'calendar.paused', color: '#FF9500', icon: 'pause-circle-outline' },
  { key: 'done', label: 'Done', translationKey: 'calendar.done', color: '#8E8E93', icon: 'checkmark-circle-outline' },
];
const ALL_STATUS_FILTERS = new Set<StatusFilter>(STATUS_FILTERS.map((filter) => filter.key));

function toDateKey(date: LuxonDateTime): string {
  return date.toISODate() || date.toFormat('yyyy-LL-dd');
}

function formatTime(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return minute === 0 ? `${displayHour}${suffix}` : `${displayHour}:${String(minute).padStart(2, '0')}${suffix}`;
}

function withAlpha(color: string, alpha: string, fallback: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : fallback;
}

function getNormalizedTags(tags?: string | null): string[] {
  if (!tags) return [];
  return tags
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);
}

function getDensityBg(count: number, isSelected: boolean, baseColor: string): string | undefined {
  if (isSelected || count === 0) return undefined;
  if (count <= 2) return withAlpha(baseColor, '05', 'rgba(15, 23, 42, 0.03)');
  if (count <= 5) return withAlpha(baseColor, '08', 'rgba(15, 23, 42, 0.05)');
  return withAlpha(baseColor, '0D', 'rgba(15, 23, 42, 0.08)');
}

function formatMonthSummaryLabel(count: number, isCompactScreen: boolean): string {
  return isCompactScreen ? `${count} total` : `${count} events`;
}

function hasScheduledTimePassed(runAtUtc?: string | null): boolean {
  if (!runAtUtc) return false;
  const runAt = DateTime.fromISO(runAtUtc);
  return runAt.isValid && runAt.toMillis() < DateTime.local().toMillis();
}

function isReminderOverdue(reminder: Reminder): boolean {
  if (reminder.is_done || reminder.is_paused) return false;
  if (reminder.classification === 'overdue') return true;
  return hasScheduledTimePassed(reminder.run_at_utc);
}

function isCalendarEventOverdue(event: CalendarEvent): boolean {
  if (event.type === 'google' || event.sent || event.isPaused) return false;
  return hasScheduledTimePassed(event.runAtUtc);
}

function eventColor(reminder: Reminder, tagColors?: Record<string, string> | null): string {
  const tagColor = getPrimaryTagColor(reminder.tags, tagColors);
  if (tagColor && !isReminderOverdue(reminder)) return tagColor;
  if (reminder.is_done) return '#8E8E93';
  if (reminder.is_paused) return '#FF9500';
  if (reminder.is_recurring) return '#3478F6';
  if (isReminderOverdue(reminder)) return OVERDUE_ACCENT;
  return '#34C759';
}

function getReminderStatusFilter(reminder: Reminder): StatusFilter {
  if (reminder.is_done) return 'done';
  if (reminder.is_paused) return 'paused';
  if (reminder.is_recurring) return 'recurring';
  if (isReminderOverdue(reminder)) return 'overdue';
  return 'active';
}

function getCalendarEventStatusFilter(event: CalendarEvent): StatusFilter {
  if (event.type === 'google') return 'google';
  if (event.sent) return 'done';
  if (event.isPaused) return 'paused';
  if (event.isRecurring) return 'recurring';
  if (isCalendarEventOverdue(event)) return 'overdue';
  return 'active';
}

function calendarEventColor(event: CalendarEvent, tagColors?: Record<string, string> | null): string {
  const tagColor = getPrimaryTagColor(event.tags, tagColors);
  if (tagColor && !isCalendarEventOverdue(event)) return tagColor;
  // Google = blue (#4285F4), deliberately NOT red — overdue + today already
  // use reds, and a red Google event was indistinguishable from "overdue".
  if (event.type === 'google') return '#4285F4';
  if (event.sent) return '#8E8E93';
  if (event.isPaused) return '#FF9500';
  if (event.isRecurring) return '#3478F6';
  if (isCalendarEventOverdue(event)) return OVERDUE_ACCENT;
  return '#34C759';
}

function calendarEventToDenseEvent(event: CalendarEvent, tagColors?: Record<string, string> | null): DenseEvent | null {
  if (!event.runAtUtc) return null;
  const runAt = DateTime.fromISO(event.runAtUtc).toLocal();
  if (!runAt.isValid) return null;
  const minutes = runAt.hour * 60 + runAt.minute;
  return {
    id: `${event.type}-${event.seq ?? event.body}-${event.runAtUtc}`,
    body: event.body,
    dateKey: toDateKey(runAt),
    minutes,
    timeLabel: formatTime(minutes),
    color: calendarEventColor(event, tagColors),
    tags: getNormalizedTags(event.tags),
    status: getCalendarEventStatusFilter(event),
    isDone: !!event.sent,
    isPaused: !!event.isPaused,
    isSynced: !!event.googleEventId && event.source !== 'google',
    isImported: event.source === 'google',
  };
}

function reminderToDenseEvent(reminder: Reminder, tagColors?: Record<string, string> | null): DenseEvent | null {
  if (!reminder.run_at_utc) return null;
  const runAt = DateTime.fromISO(reminder.run_at_utc).toLocal();
  if (!runAt.isValid) return null;
  const minutes = runAt.hour * 60 + runAt.minute;
  const status = getReminderStatusFilter(reminder);
  return {
    id: `${reminder.user_seq}-${reminder.run_at_utc}`,
    body: reminder.body,
    dateKey: toDateKey(runAt),
    minutes,
    timeLabel: formatTime(minutes),
    color: eventColor(reminder, tagColors),
    tags: getNormalizedTags(reminder.tags),
    status,
    isDone: !!reminder.is_done,
    isPaused: !!reminder.is_paused,
    isSynced: !!reminder.google_event_id && reminder.source !== 'google',
    isImported: reminder.source === 'google',
  };
}

function reminderToCalendarEvent(reminder: Reminder): CalendarEvent | null {
  if (!reminder.run_at_utc) return null;
  return {
    type: 'reminder',
    seq: reminder.user_seq,
    body: reminder.body,
    runAtUtc: reminder.run_at_utc,
    isRecurring: !!reminder.is_recurring,
    isPaused: !!reminder.is_paused,
    sent: !!(reminder.is_done && !reminder.is_recurring),
    tags: reminder.tags,
    durationMinutes: reminder.duration_minutes ?? null,
    colPref: reminder.col_pref ?? null,
    recurJson: reminder.recur_json,
    untilUtc: reminder.until_utc,
    googleEventId: reminder.google_event_id ?? null,
    source: reminder.source ?? 'app',
  };
}

function buildWeeks(month: LuxonDateTime): LuxonDateTime[][] {
  const firstDay = month.startOf('month');
  const start = firstDay.minus({ days: firstDay.weekday % 7 }).startOf('day');
  const leadingDays = firstDay.weekday % 7;
  const weekCount = Math.ceil((leadingDays + (month.daysInMonth ?? 31)) / 7);
  return Array.from({ length: weekCount }, (_, weekIndex) => (
    Array.from({ length: 7 }, (_, dayIndex) => start.plus({ days: weekIndex * 7 + dayIndex }))
  ));
}

export default function CalendarMonthV2Screen({
  additionalCalendarEvents = [],
  onQuickAdd,
  onResizeEvent,
  onMoveEvent,
  onChangeColumn,
  onDragCreate,
  onRefresh,
  refreshing,
  showPrototypeNav = true,
  reservedBottomSpace,
  onPrototypeNavRoutePress,
  focusRequest,
}: CalendarMonthV2ScreenProps = {}) {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const { width, height } = useWindowDimensions();
  const isNarrowScreen = width <= 380;
  const isCompactScreen = width <= 340;
  const monthSceneTravel = Math.max(width, 1);
  const filterIconSize = isCompactScreen ? 11 : isNarrowScreen ? 11 : 12;
  const { colors, isDark } = useTheme();
  const prototypePalette = isDark ? PROTOTYPE_DARK : PROTOTYPE_LIGHT;
  const styles = useMemo(() => createStyles(colors, prototypePalette, { isDark, isNarrowScreen, isCompactScreen }), [colors, isCompactScreen, isDark, isNarrowScreen, prototypePalette]);
  const insets = useSafeAreaInsets();
  const reminders = useRemindersStore((state) => state.reminders);
  const remindersLoading = useRemindersStore((state) => state.isLoading);
  const syncFromServer = useRemindersStore((state) => state.syncFromServer);
  const tagColors = useAuthStore((state) => state.user?.tagColors ?? null);
  const storedSelectedDate = useCalendarStore((state) => state.selectedDate);
  const setStoredSelectedDate = useCalendarStore((state) => state.setSelectedDate);
  const setTabBarChrome = useNavigationStore((state) => state.setTabBarChrome);
  const [selectedDate, setSelectedDate] = useState(() => storedSelectedDate || toDateKey(DateTime.local()));
  const [currentMonth, setCurrentMonth] = useState(() => {
    const parsed = DateTime.fromISO(storedSelectedDate || '');
    return (parsed.isValid ? parsed : DateTime.local()).startOf('month');
  });
  const [transitionMonth, setTransitionMonth] = useState<LuxonDateTime | null>(null);
  const [monthTransitionDirection, setMonthTransitionDirection] = useState<-1 | 1>(1);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [dayPanelOpen, setDayPanelOpen] = useState(false);
  const [renderDayPanel, setRenderDayPanel] = useState(false);
  const [isHeroCollapsed, setIsHeroCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [activeStatusFilters, setActiveStatusFilters] = useState<Set<StatusFilter>>(() => new Set(ALL_STATUS_FILTERS));
  const [disabledTagFilters, setDisabledTagFilters] = useState<Set<string>>(new Set());
  const [magnetAnchor, setMagnetAnchor] = useState<GridPosition | null>(null);
  const [preloadFarMonth, setPreloadFarMonth] = useState(true);
  const headerGesture = useMemo(() => Gesture.Pan().enabled(false), []);
  const daySwipeGesture = useMemo(() => Gesture.Pan().enabled(false), []);
  const headerAnimatedStyle = useAnimatedStyle(() => ({}));
  const contentAnimatedStyle = useAnimatedStyle(() => ({}));
  const eventsSwipeAnimatedStyle = useAnimatedStyle(() => ({}));
  const handledFocusRequestIdRef = useRef<number | null>(null);
  const magnetProgress = useRef(new Animated.Value(0)).current;
  const monthTransitionProgress = useRef(new Animated.Value(0)).current;
  const monthScenePositionsRef = useRef(new Map<string, Animated.Value>());
  const dayPanelProgress = useRef(new Animated.Value(0)).current;
  const wasDayPanelOpenRef = useRef(false);
  const yearPickerScrollRef = useRef<ScrollView | null>(null);
  const isMonthTransitioningRef = useRef(false);
  const completedAdjacentMonthKeyRef = useRef<string | null>(null);
  const deferredFarMonthDirectionRef = useRef<-1 | 1 | null>(null);
  const currentMonthRef = useRef(currentMonth);
  const requestedMonthRef = useRef(currentMonth);
  const queuedMonthRef = useRef<LuxonDateTime | null>(null);
  const preloadFarMonthRef = useRef(preloadFarMonth);
  const cachedMonthSceneTravelRef = useRef(monthSceneTravel);
  const monthSceneTravelChangedRef = useRef(false);
  currentMonthRef.current = currentMonth;
  preloadFarMonthRef.current = preloadFarMonth;
  if (cachedMonthSceneTravelRef.current !== monthSceneTravel) {
    // Fold/unfold and split-screen changes preserve the mounted calendar, but
    // neighbour scenes cached at the old width are no longer fully offscreen.
    // Rebuild their positions during this render so stale previous/next grids
    // cannot overlap the current month for even one committed frame.
    monthScenePositionsRef.current.clear();
    cachedMonthSceneTravelRef.current = monthSceneTravel;
    monthSceneTravelChangedRef.current = true;
  }
  const getMonthScenePosition = useCallback((month: LuxonDateTime, initialValue: number) => {
    const key = month.toFormat('yyyy-MM');
    const cachedPosition = monthScenePositionsRef.current.get(key);
    if (cachedPosition) return cachedPosition;
    const position = new Animated.Value(initialValue);
    monthScenePositionsRef.current.set(key, position);
    return position;
  }, []);

  useEffect(() => {
    syncFromServer().catch(() => undefined);
  }, [syncFromServer]);

  useEffect(() => {
    if (!yearPickerOpen) return;

    const frame = requestAnimationFrame(() => {
      yearPickerScrollRef.current?.scrollTo({
        y: Math.max(0, (YEAR_PICKER_RADIUS - 2) * YEAR_PICKER_ITEM_HEIGHT),
        animated: false,
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [currentMonth.year, yearPickerOpen]);

  useEffect(() => {
    if (showPrototypeNav) return;
    setNavOpen(false);
  }, [showPrototypeNav]);

  const allCalendarEvents = useMemo(() => {
    const reminderEvents = reminders
      .map(reminderToCalendarEvent)
      .filter((event): event is CalendarEvent => !!event);

    return [...reminderEvents, ...additionalCalendarEvents]
      .filter((event) => !!event.runAtUtc)
      .sort((a, b) => a.runAtUtc.localeCompare(b.runAtUtc));
  }, [additionalCalendarEvents, reminders]);

  const allDenseEvents = useMemo(() => allCalendarEvents
    .map((event) => calendarEventToDenseEvent(event, tagColors))
    .filter((event): event is DenseEvent => !!event)
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey) || a.minutes - b.minutes),
  [allCalendarEvents, tagColors]);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    allDenseEvents.forEach((event) => {
      event.tags.forEach((tag) => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [allDenseEvents]);

  const localizedWeekdayLabels = useMemo(
    () => Array.from({ length: 7 }, (_, index) => DateTime.local(2024, 6, 2 + index).setLocale(locale).toFormat('ccc')),
    [locale],
  );

  const localizedStatusFilters = useMemo(
    () => STATUS_FILTERS.map((filter) => ({ ...filter, label: t(filter.translationKey, filter.label) })),
    [t],
  );

  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, monthIndex) => ({
    index: monthIndex + 1,
    label: DateTime.local(currentMonth.year, monthIndex + 1, 1).setLocale(locale).toFormat(isNarrowScreen ? 'LLL' : 'LLLL'),
    fullLabel: DateTime.local(currentMonth.year, monthIndex + 1, 1).setLocale(locale).toFormat('LLLL'),
  })), [currentMonth, isNarrowScreen, locale]);

  const monthPickerRows = useMemo(() => Array.from({ length: 4 }, (_, rowIndex) => (
    monthOptions.slice(rowIndex * 3, rowIndex * 3 + 3)
  )), [monthOptions]);

  const yearPickerYears = useMemo(
    () => Array.from({ length: YEAR_PICKER_RADIUS * 2 + 1 }, (_, index) => currentMonth.year - YEAR_PICKER_RADIUS + index),
    [currentMonth.year],
  );

  const tagFilteredDenseEvents = useMemo(() => allDenseEvents.filter((event) => !event.tags.some((tag) => disabledTagFilters.has(tag))), [allDenseEvents, disabledTagFilters]);
  const tagFilteredCalendarEvents = useMemo(() => allCalendarEvents.filter((event) => !getNormalizedTags(event.tags).some((tag) => disabledTagFilters.has(tag))), [allCalendarEvents, disabledTagFilters]);

  // "Google" is a sync OVERLAY, not a mutually-exclusive status: a synced
  // reminder still has its real status (overdue/active/…). So an event shows
  // when its status is active OR (it's synced and the Google filter is on).
  const events = useMemo(() => tagFilteredDenseEvents.filter((event) =>
    activeStatusFilters.has(event.status) || ((event.isSynced || event.isImported) && activeStatusFilters.has('google'))
  ), [activeStatusFilters, tagFilteredDenseEvents]);
  const calendarEvents = useMemo(() => tagFilteredCalendarEvents.filter((event) =>
    activeStatusFilters.has(getCalendarEventStatusFilter(event)) || ((!!event.googleEventId || event.source === 'google') && activeStatusFilters.has('google'))
  ), [activeStatusFilters, tagFilteredCalendarEvents]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, DenseEvent[]>();
    events.forEach((event) => {
      const list = map.get(event.dateKey) ?? [];
      list.push(event);
      map.set(event.dateKey, list);
    });
    return map;
  }, [events]);

  const previousMonth = useMemo(() => currentMonth.minus({ months: 1 }).startOf('month'), [currentMonth]);
  const nextMonth = useMemo(() => currentMonth.plus({ months: 1 }).startOf('month'), [currentMonth]);
  const previousWeeks = useMemo(() => buildWeeks(previousMonth), [previousMonth]);
  const weeks = useMemo(() => buildWeeks(currentMonth), [currentMonth]);
  const nextWeeks = useMemo(() => buildWeeks(nextMonth), [nextMonth]);
  const transitionWeeks = useMemo(() => (transitionMonth ? buildWeeks(transitionMonth) : null), [transitionMonth]);
  const expandedAnchor = useMemo<GridPosition | null>(() => {
    if (!expandedDate) return null;
    for (let weekIndex = 0; weekIndex < weeks.length; weekIndex += 1) {
      const dayIndex = weeks[weekIndex].findIndex((day) => day.hasSame(currentMonth, 'month') && toDateKey(day) === expandedDate);
      if (dayIndex >= 0) return { weekIndex, dayIndex };
    }
    return null;
  }, [currentMonth, expandedDate, weeks]);
  const activeMagnetAnchor = expandedAnchor ?? magnetAnchor;
  const todayKey = toDateKey(DateTime.local());
  const selectedDayEvents = eventsByDate.get(selectedDate) ?? [];
  // The navigator is a complete schedule surface, so keep it consistent with
  // the global sheet opened from Chat or Reminders. Month status chips filter
  // the month grid only; tag visibility still applies everywhere.
  const selectedCalendarEvents = useMemo(() => tagFilteredCalendarEvents.filter((event) => DateTime.fromISO(event.runAtUtc).toLocal().toISODate() === selectedDate), [selectedDate, tagFilteredCalendarEvents]);
  const selectedDateTime = DateTime.fromISO(selectedDate);
  const selectedWeekdayLabel = selectedDateTime.isValid ? selectedDateTime.setLocale(locale).toFormat('cccc') : '';
  const selectedDateDisplay = selectedDateTime.isValid ? selectedDateTime.setLocale(locale).toFormat('LLLL d') : selectedDate;
  const selectedDateLabel = selectedDateTime.isValid ? selectedDateTime.setLocale(locale).toFormat('cccc, LLLL d') : selectedDate;
  const getMonthEventCount = useCallback((month: LuxonDateTime) => events.filter((event) => {
    const eventDate = DateTime.fromISO(event.dateKey);
    return eventDate.isValid && eventDate.hasSame(month, 'month');
  }).length, [events]);
  const monthEventCount = useMemo(() => getMonthEventCount(currentMonth), [currentMonth, getMonthEventCount]);
  const filterStats = useMemo(() => {
    const stats: Record<StatusFilter, number> = { overdue: 0, active: 0, recurring: 0, paused: 0, done: 0, google: 0 };
    tagFilteredDenseEvents.forEach((event) => {
      const eventDate = DateTime.fromISO(event.dateKey);
      if (eventDate.isValid && eventDate.hasSame(currentMonth, 'month')) {
        stats[event.status] += 1;
        // Google is an overlay count: every synced OR imported event adds to
        // it, regardless of its primary status.
        if (event.isSynced || event.isImported) stats.google += 1;
      }
    });
    return stats;
  }, [currentMonth, tagFilteredDenseEvents]);
  const isStatusFilterActive = activeStatusFilters.size < ALL_STATUS_FILTERS.size;
  const monthTitleLabel = currentMonth.setLocale(locale).toFormat(isCompactScreen ? 'LLL yyyy' : 'LLLL yyyy');
  const monthSummaryLabel = formatMonthSummaryLabel(monthEventCount, isCompactScreen);
  const previousMonthTitleLabel = previousMonth.setLocale(locale).toFormat(isCompactScreen ? 'LLL yyyy' : 'LLLL yyyy');
  const previousMonthSummaryLabel = formatMonthSummaryLabel(getMonthEventCount(previousMonth), isCompactScreen);
  const nextMonthTitleLabel = nextMonth.setLocale(locale).toFormat(isCompactScreen ? 'LLL yyyy' : 'LLLL yyyy');
  const nextMonthSummaryLabel = formatMonthSummaryLabel(getMonthEventCount(nextMonth), isCompactScreen);
  const transitionMonthTitleLabel = transitionMonth
    ? transitionMonth.setLocale(locale).toFormat(isCompactScreen ? 'LLL yyyy' : 'LLLL yyyy')
    : '';
  const transitionMonthSummaryLabel = transitionMonth
    ? formatMonthSummaryLabel(getMonthEventCount(transitionMonth), isCompactScreen)
    : '';

  const collapsedBottomChrome = 28 + Math.max(insets.bottom, 6);
  const expandedBottomChrome = 82 + (insets.bottom > 0 ? insets.bottom : 8);
  const fallbackBottomChrome = reservedBottomSpace ?? (56 + Math.max(insets.bottom, 6));
  const bottomChrome = showPrototypeNav ? (navOpen ? expandedBottomChrome : collapsedBottomChrome) : fallbackBottomChrome;
  const headerLayoutReserve = (isCompactScreen ? 156 : 176) + (isHeroCollapsed ? 0 : 12);
  const dayPanelTop = insets.top;

  useEffect(() => {
    if (showPrototypeNav) return;

    if (dayPanelOpen) {
      setTabBarChrome({ expanded: false, bottomSpace: collapsedBottomChrome });
    } else if (wasDayPanelOpenRef.current) {
      setTabBarChrome({ expanded: true, bottomSpace: expandedBottomChrome });
    }
    wasDayPanelOpenRef.current = dayPanelOpen;
  }, [collapsedBottomChrome, dayPanelOpen, expandedBottomChrome, setTabBarChrome, showPrototypeNav]);
  const expandedSideBleed = isCompactScreen ? 12 : isNarrowScreen ? 16 : 18;
  const expandedBottomBleed = isCompactScreen ? 4 : 6;
  const expandedHeaderHeight = isCompactScreen ? 18 : 20;
  const expandedListTopPadding = isCompactScreen ? 3 : 4;
  const expandedEventHeight = isCompactScreen ? 15 : 16;
  const expandedEventGap = 1;
  const dayPanelLift = isCompactScreen ? 36 : 44;
  const baseDayCellSurfaceColor = prototypePalette.dayCellSurface;
  const densityTintColor = prototypePalette.densityTint;
  const eventPillFallbackColor = prototypePalette.eventFallback;
  const magnetHorizontalPush = isCompactScreen ? 12 : isNarrowScreen ? 16 : 18;
  const magnetHorizontalFalloff = isCompactScreen ? 3 : 4;
  const magnetVerticalPush = isCompactScreen ? 26 : isNarrowScreen ? 30 : 34;
  const magnetVerticalFalloff = isCompactScreen ? 9 : 11;
  const pendingTapRef = useRef<{ key: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const longPressGuardRef = useRef<{ key: string; timestamp: number } | null>(null);
  const handleQuickAdd = useCallback((dateKey?: string) => {
    if (onQuickAdd) {
      onQuickAdd(dateKey);
      return;
    }
    router.replace('/(tabs)/calendar' as never);
  }, [onQuickAdd, router]);
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
      return;
    }
    syncFromServer().catch(() => undefined);
  }, [onRefresh, syncFromServer]);
  const handleResizeEvent = useCallback((seq: number, durationMinutes: number) => {
    if (!onResizeEvent) return;
    return onResizeEvent(seq, durationMinutes);
  }, [onResizeEvent]);
  const handleMoveEvent = useCallback((seq: number, newRunAtUtc: string) => {
    if (!onMoveEvent) return;
    return onMoveEvent(seq, newRunAtUtc);
  }, [onMoveEvent]);
  const handleChangeColumn = useCallback((updates: Array<{ seq: number; colPref: number }>) => {
    if (!onChangeColumn) return;
    return onChangeColumn(updates);
  }, [onChangeColumn]);
  const handlePrototypeNavRoutePress = useCallback((route: string) => {
    if (onPrototypeNavRoutePress) {
      onPrototypeNavRoutePress(route);
      return;
    }
    router.replace(route as never);
  }, [onPrototypeNavRoutePress, router]);
  const isRefreshing = refreshing ?? remindersLoading;

  const clearPendingTap = useCallback(() => {
    if (!pendingTapRef.current) return;
    clearTimeout(pendingTapRef.current.timer);
    pendingTapRef.current = null;
  }, []);

  useEffect(() => () => {
    clearPendingTap();
  }, [clearPendingTap]);

  useEffect(() => () => {
    monthTransitionProgress.stopAnimation();
    monthScenePositionsRef.current.forEach((position) => position.stopAnimation());
    dayPanelProgress.stopAnimation();
  }, [dayPanelProgress, monthTransitionProgress]);

  useLayoutEffect(() => {
    const completedMonthKey = completedAdjacentMonthKeyRef.current;
    if (
      !completedMonthKey
      || completedMonthKey !== currentMonth.toFormat('yyyy-MM')
    ) return;
    completedAdjacentMonthKeyRef.current = null;
    isMonthTransitioningRef.current = false;
  }, [currentMonth]);

  useLayoutEffect(() => {
    if (preloadFarMonth) deferredFarMonthDirectionRef.current = null;
  }, [preloadFarMonth]);

  useEffect(() => {
    if (preloadFarMonth) return undefined;
    const frame = requestAnimationFrame(() => {
      const direction = deferredFarMonthDirectionRef.current;
      if (direction) {
        const farMonth = currentMonth.plus({ months: direction }).startOf('month');
        getMonthScenePosition(farMonth, direction * monthSceneTravel).setValue(direction * monthSceneTravel);
      }
      setPreloadFarMonth(true);
    });
    return () => cancelAnimationFrame(frame);
  }, [currentMonth, getMonthScenePosition, monthSceneTravel, preloadFarMonth]);

  useEffect(() => {
    if (!expandedAnchor) return;
    setMagnetAnchor(expandedAnchor);
  }, [expandedAnchor]);

  useEffect(() => {
    dayPanelProgress.stopAnimation();
    if (dayPanelOpen && !renderDayPanel) {
      // Mount the sheet at its starting position first. Starting the animation
      // before the sheet exists lets the value advance off-screen, then the
      // renderDayPanel update restarts it and produces a visible flash/jump.
      dayPanelProgress.setValue(0);
      setRenderDayPanel(true);
      return;
    }
    if (dayPanelOpen) {
      Animated.timing(dayPanelProgress, {
        toValue: 1,
        duration: DAY_PANEL_ANIMATION_DURATION,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
      return;
    }
    if (!renderDayPanel) return;
    Animated.timing(dayPanelProgress, {
      toValue: 0,
      duration: 150,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        setRenderDayPanel(false);
      }
    });
  }, [dayPanelOpen, dayPanelProgress, renderDayPanel]);

  useEffect(() => {
    magnetProgress.stopAnimation();
    Animated.spring(magnetProgress, {
      toValue: expandedDate ? 1 : 0,
      tension: expandedDate ? 112 : 94,
      friction: expandedDate ? 9 : 8,
      useNativeDriver: true,
    }).start();
  }, [expandedDate, magnetProgress]);

  const animateMonthChange = useCallback((nextMonth: LuxonDateTime) => {
    const activeMonth = currentMonthRef.current;
    const normalizedMonth = nextMonth.startOf('month');
    requestedMonthRef.current = normalizedMonth;
    if (isMonthTransitioningRef.current) {
      queuedMonthRef.current = normalizedMonth;
      return;
    }
    if (normalizedMonth.hasSame(activeMonth, 'month')) {
      queuedMonthRef.current = null;
      return;
    }
    queuedMonthRef.current = null;

    const direction: -1 | 1 = normalizedMonth.toMillis() > activeMonth.toMillis() ? 1 : -1;
    const monthOffset = ((normalizedMonth.year - activeMonth.year) * 12) + normalizedMonth.month - activeMonth.month;
    isMonthTransitioningRef.current = true;
    monthTransitionProgress.stopAnimation();
    monthTransitionProgress.setValue(0);

    if (Math.abs(monthOffset) === 1) {
      if (!preloadFarMonthRef.current && deferredFarMonthDirectionRef.current === direction) {
        isMonthTransitioningRef.current = false;
        queuedMonthRef.current = normalizedMonth;
        return;
      }

      const outgoingPosition = getMonthScenePosition(activeMonth, 0);
      const incomingPosition = getMonthScenePosition(normalizedMonth, direction * monthSceneTravel);
      outgoingPosition.stopAnimation();
      incomingPosition.stopAnimation();
      outgoingPosition.setValue(0);
      incomingPosition.setValue(direction * monthSceneTravel);

      Animated.parallel([
        Animated.timing(outgoingPosition, {
          toValue: -direction * monthSceneTravel,
          duration: MONTH_SWAP_DURATION,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(incomingPosition, {
          toValue: 0,
          duration: MONTH_SWAP_DURATION,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start(({ finished }) => {
        if (!finished) {
          isMonthTransitioningRef.current = false;
          return;
        }
        deferredFarMonthDirectionRef.current = direction;
        completedAdjacentMonthKeyRef.current = normalizedMonth.toFormat('yyyy-MM');
        setPreloadFarMonth(false);
        setCurrentMonth(normalizedMonth);
      });
      return;
    }

    setMonthTransitionDirection(direction);
    setTransitionMonth(normalizedMonth);
  }, [getMonthScenePosition, monthSceneTravel, monthTransitionProgress]);

  useEffect(() => {
    if (!transitionMonth) return undefined;

    // transitionMonth is now mounted, so the native-thread animation can start
    // without losing its opening frames while React builds the incoming grid.
    const animation = Animated.timing(monthTransitionProgress, {
      toValue: 1,
      duration: MONTH_SWAP_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });

    animation.start(({ finished }) => {
      if (!finished) {
        isMonthTransitioningRef.current = false;
        return;
      }
      // A far target may already exist in the cache as an old offscreen
      // neighbour. Reusing that Animated.Value after promoting it to current
      // makes the completed grid jump away and exposes the previous month.
      // The transition overlay has its own progress value, so it remains
      // pinned while the canonical previous/current/next positions rebuild.
      monthScenePositionsRef.current.clear();
      setCurrentMonth(transitionMonth);
      setTransitionMonth(null);
      isMonthTransitioningRef.current = false;
    });

    return () => animation.stop();
  }, [monthTransitionProgress, transitionMonth]);

  useEffect(() => {
    if (!preloadFarMonth || transitionMonth || isMonthTransitioningRef.current || !queuedMonthRef.current) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      if (isMonthTransitioningRef.current) return;
      const queuedMonth = queuedMonthRef.current;
      if (!queuedMonth) return;
      queuedMonthRef.current = null;
      animateMonthChange(queuedMonth);
    });
    return () => cancelAnimationFrame(frame);
  }, [animateMonthChange, currentMonth, preloadFarMonth, transitionMonth]);

  const setMonthDirect = useCallback((nextMonth: LuxonDateTime) => {
    const normalizedMonth = nextMonth.startOf('month');
    isMonthTransitioningRef.current = false;
    requestedMonthRef.current = normalizedMonth;
    queuedMonthRef.current = null;
    completedAdjacentMonthKeyRef.current = null;
    deferredFarMonthDirectionRef.current = null;
    monthTransitionProgress.stopAnimation();
    monthTransitionProgress.setValue(0);
    monthScenePositionsRef.current.forEach((position) => position.stopAnimation());
    monthScenePositionsRef.current.clear();
    setPreloadFarMonth(true);
    setTransitionMonth(null);
    setCurrentMonth(normalizedMonth);
    setExpandedDate(null);
    setDayPanelOpen(false);
  }, [monthTransitionProgress]);

  useLayoutEffect(() => {
    if (!monthSceneTravelChangedRef.current) return;
    monthSceneTravelChangedRef.current = false;
    if (!isMonthTransitioningRef.current && !transitionMonth) return;

    // Resizing detaches an adjacent scene's old-width native animation. Keep
    // the user's navigation intent by committing its requested destination in
    // the newly measured layout instead of dropping the tap mid-fold.
    setMonthDirect(requestedMonthRef.current);
  }, [monthSceneTravel, setMonthDirect, transitionMonth]);

  useEffect(() => {
    if (!storedSelectedDate || storedSelectedDate === selectedDate) return;
    const parsed = DateTime.fromISO(storedSelectedDate);
    if (!parsed.isValid) return;
    setSelectedDate(storedSelectedDate);
    if (!parsed.hasSame(currentMonth, 'month')) setMonthDirect(parsed.startOf('month'));
  }, [currentMonth, selectedDate, setMonthDirect, storedSelectedDate]);

  useEffect(() => {
    if (!focusRequest) return;
    if (handledFocusRequestIdRef.current === focusRequest.id) return;
    const parsed = DateTime.fromISO(focusRequest.dateKey);
    if (!parsed.isValid) return;
    handledFocusRequestIdRef.current = focusRequest.id;
    setSelectedDate(focusRequest.dateKey);
    setStoredSelectedDate(focusRequest.dateKey);
    if (!parsed.hasSame(currentMonth, 'month')) {
      if (focusRequest.animateMonthChange) animateMonthChange(parsed.startOf('month'));
      else setMonthDirect(parsed.startOf('month'));
    }
    setExpandedDate(focusRequest.dateKey);
    setDayPanelOpen(focusRequest.openDayPanel ?? true);
    if (focusRequest.viewMode) setViewMode(focusRequest.viewMode);
  }, [animateMonthChange, currentMonth, focusRequest, setMonthDirect, setStoredSelectedDate]);

  const getMagnetMotionStyle = useCallback((weekIndex: number, dayIndex: number, isExpanded: boolean) => {
    if (!activeMagnetAnchor) return null;
    const rowDelta = weekIndex - activeMagnetAnchor.weekIndex;
    const colDelta = dayIndex - activeMagnetAnchor.dayIndex;

    if (rowDelta === 0 && colDelta === 0) {
      if (!isExpanded) return null;
      return {
        transform: [
          {
            scale: magnetProgress.interpolate({
              inputRange: [0, 1],
              outputRange: [1, 1.025],
            }),
          },
        ],
      };
    }

    const absRow = Math.abs(rowDelta);
    const absCol = Math.abs(colDelta);
    const horizontalBase = absCol === 0 ? 0 : Math.max(0, magnetHorizontalPush - ((absCol - 1) * magnetHorizontalFalloff));
    const verticalBase = absRow === 0 ? 0 : Math.max(0, magnetVerticalPush - ((absRow - 1) * magnetVerticalFalloff));

    let translateXTarget = 0;
    let translateYTarget = 0;
    if (rowDelta === 0 && colDelta !== 0) {
      translateXTarget = Math.sign(colDelta) * horizontalBase;
    } else if (rowDelta !== 0 && colDelta === 0) {
      translateYTarget = Math.sign(rowDelta) * verticalBase;
    } else if (rowDelta !== 0 && colDelta !== 0) {
      translateXTarget = Math.sign(colDelta) * Math.max(4, horizontalBase * 0.7);
      translateYTarget = Math.sign(rowDelta) * Math.max(10, verticalBase * 0.86);
    }

    const scaleTarget = absRow + absCol <= 1 ? 0.965 : 0.982;
    const opacityTarget = rowDelta === 0 ? 0.95 : 0.91;

    return {
      opacity: magnetProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [1, opacityTarget],
      }),
      transform: [
        {
          translateX: magnetProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [0, translateXTarget],
          }),
        },
        {
          translateY: magnetProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [0, translateYTarget],
          }),
        },
        {
          scale: magnetProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [1, scaleTarget],
          }),
        },
      ],
    };
  }, [activeMagnetAnchor, magnetHorizontalFalloff, magnetHorizontalPush, magnetProgress, magnetVerticalFalloff, magnetVerticalPush]);

  const toggleStatusFilter = useCallback((status: StatusFilter) => {
    setActiveStatusFilters((current) => {
      const next = new Set(current);
      if (next.has(status)) {
        if (next.size > 1) next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const toggleTagFilter = useCallback((tag: string) => {
    setDisabledTagFilters((current) => {
      const next = new Set(current);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const openDayPanelForDate = useCallback((date: LuxonDateTime) => {
    const key = toDateKey(date);
    setSelectedDate(key);
    setStoredSelectedDate(key);
    if (!date.hasSame(currentMonthRef.current, 'month')) animateMonthChange(date.startOf('month'));
    setExpandedDate(key);
    setDayPanelOpen(true);
    setViewMode((current) => current || 'month');
  }, [animateMonthChange, setStoredSelectedDate]);

  const collapseExpandedPreview = useCallback(() => {
    clearPendingTap();
    setExpandedDate(null);
    setDayPanelOpen(false);
  }, [clearPendingTap]);

  const applySingleDateTap = useCallback((date: LuxonDateTime) => {
    const key = toDateKey(date);
    const shouldCollapse = expandedDate === key && !dayPanelOpen;
    setSelectedDate(key);
    setStoredSelectedDate(key);
    if (!date.hasSame(currentMonthRef.current, 'month')) animateMonthChange(date.startOf('month'));
    if (shouldCollapse) {
      setExpandedDate(null);
      setDayPanelOpen(false);
      return;
    }
    setExpandedDate(key);
    setDayPanelOpen(false);
  }, [animateMonthChange, dayPanelOpen, expandedDate, setStoredSelectedDate]);

  const handleDatePress = useCallback((date: LuxonDateTime) => {
    const key = toDateKey(date);
    const longPressGuard = longPressGuardRef.current;
    if (longPressGuard?.key === key && Date.now() - longPressGuard.timestamp < 700) {
      longPressGuardRef.current = null;
      return;
    }
    if (pendingTapRef.current?.key === key) {
      clearPendingTap();
      openDayPanelForDate(date);
      return;
    }
    clearPendingTap();
    pendingTapRef.current = {
      key,
      timer: setTimeout(() => {
        pendingTapRef.current = null;
        applySingleDateTap(date);
      }, DAY_CELL_DOUBLE_TAP_MS),
    };
  }, [applySingleDateTap, clearPendingTap, openDayPanelForDate]);

  const handleDateLongPress = useCallback((date: LuxonDateTime) => {
    const key = toDateKey(date);
    clearPendingTap();
    longPressGuardRef.current = { key, timestamp: Date.now() };
    setSelectedDate(key);
    setStoredSelectedDate(key);
    if (!date.hasSame(currentMonthRef.current, 'month')) animateMonthChange(date.startOf('month'));
    handleQuickAdd(key);
  }, [animateMonthChange, clearPendingTap, handleQuickAdd, setStoredSelectedDate]);

  const selectDateKey = useCallback((dateKey: string) => {
    const parsed = DateTime.fromISO(dateKey);
    if (!parsed.isValid) return;
    clearPendingTap();
    setSelectedDate(dateKey);
    setStoredSelectedDate(dateKey);
    animateMonthChange(parsed.startOf('month'));
    setExpandedDate(dateKey);
    setDayPanelOpen(true);
  }, [animateMonthChange, clearPendingTap, setStoredSelectedDate]);

  const jumpToToday = useCallback(() => {
    const today = DateTime.local();
    const key = toDateKey(today);
    clearPendingTap();
    setSelectedDate(key);
    setStoredSelectedDate(key);
    animateMonthChange(today.startOf('month'));
    setExpandedDate(key);
    setDayPanelOpen(false);
  }, [animateMonthChange, clearPendingTap, setStoredSelectedDate]);

  const shiftMonth = useCallback((direction: -1 | 1) => {
    clearPendingTap();
    const requestedMonth = requestedMonthRef.current.plus({ months: direction });
    requestedMonthRef.current = requestedMonth;
    animateMonthChange(requestedMonth);
    setExpandedDate(null);
    setDayPanelOpen(false);
  }, [animateMonthChange, clearPendingTap]);

  const openMonthPicker = useCallback(() => {
    clearPendingTap();
    setYearPickerOpen(false);
    setMonthPickerOpen(true);
  }, [clearPendingTap]);

  const closeMonthPicker = useCallback(() => {
    setYearPickerOpen(false);
    setMonthPickerOpen(false);
  }, []);

  const handleSelectMonthFromPicker = useCallback((monthIndex: number) => {
    setMonthDirect(currentMonth.set({ month: monthIndex, day: 1 }));
    setYearPickerOpen(false);
    setMonthPickerOpen(false);
  }, [currentMonth, setMonthDirect]);

  const toggleYearPicker = useCallback(() => {
    setYearPickerOpen((open) => !open);
  }, []);

  const handleSelectYearFromPicker = useCallback((year: number) => {
    setMonthDirect(currentMonth.set({ year, day: 1 }));
    setYearPickerOpen(false);
  }, [currentMonth, setMonthDirect]);

  const stepMonthPickerYear = useCallback((direction: -1 | 1) => {
    setMonthDirect(currentMonth.plus({ years: direction }));
  }, [currentMonth, setMonthDirect]);

  const monthSwipeGesture = useMemo(
    () => Gesture.Pan()
      .activeOffsetX([-MONTH_SWIPE_ACTIVATION_DISTANCE, MONTH_SWIPE_ACTIVATION_DISTANCE])
      .failOffsetY([-24, 24])
      .maxPointers(1)
      .onEnd((event) => {
        const dx = event.translationX;
        const vx = event.velocityX;
        if (dx <= -MONTH_SWIPE_DISTANCE || vx <= -MONTH_SWIPE_VELOCITY) {
          runOnJS(shiftMonth)(1);
        } else if (dx >= MONTH_SWIPE_DISTANCE || vx >= MONTH_SWIPE_VELOCITY) {
          runOnJS(shiftMonth)(-1);
        }
      }),
    [shiftMonth],
  );

  const navigateDay = useCallback((direction: -1 | 1) => {
    const base = DateTime.fromISO(useCalendarStore.getState().selectedDate || selectedDate);
    const next = (base.isValid ? base : DateTime.local()).plus({ days: direction });
    selectDateKey(toDateKey(next));
  }, [selectDateKey, selectedDate]);

  const navigateWeek = useCallback((direction: -1 | 1) => {
    const base = DateTime.fromISO(useCalendarStore.getState().selectedDate || selectedDate);
    const next = (base.isValid ? base : DateTime.local()).plus({ days: direction * 7 });
    selectDateKey(toDateKey(next));
  }, [selectDateKey, selectedDate]);

  const collapseHero = useCallback(() => {
    if (isHeroCollapsed) return;
    setIsHeroCollapsed(true);
  }, [isHeroCollapsed]);

  const expandHero = useCallback(() => {
    if (!isHeroCollapsed) return;
    setIsHeroCollapsed(false);
  }, [isHeroCollapsed]);

  const toggleHero = useCallback(() => {
    setIsHeroCollapsed((current) => !current);
  }, []);

  const heroHandleGesture = useMemo(
    () => Gesture.Pan()
      .activeOffsetY([-8, 8])
      .failOffsetX([-20, 20])
      .onEnd((event) => {
        const dy = event.translationY;
        const vy = event.velocityY;
        if (dy < -8 || vy < -200) {
          runOnJS(collapseHero)();
        } else if (dy > 8 || vy > 200) {
          runOnJS(expandHero)();
        }
      }),
    [collapseHero, expandHero],
  );

  useBackHandler({
    id: 'calendar-month-v2',
    label: 'Calendar',
    priority: 800,
    enabled: yearPickerOpen || monthPickerOpen || (showPrototypeNav && navOpen) || dayPanelOpen || renderDayPanel || !!expandedDate || isHeroCollapsed,
    onBack: () => {
      if (yearPickerOpen) {
        setYearPickerOpen(false);
        return true;
      }
      if (monthPickerOpen) {
        closeMonthPicker();
        return true;
      }
      if (showPrototypeNav && navOpen) {
        setNavOpen(false);
        return true;
      }
      if (dayPanelOpen) {
        if (viewMode !== 'month') {
          setViewMode('month');
          return true;
        }
        setDayPanelOpen(false);
        return true;
      }
      if (renderDayPanel) {
        setDayPanelOpen(false);
        return true;
      }
      if (expandedDate) {
        collapseExpandedPreview();
        return true;
      }
      if (isHeroCollapsed) {
        expandHero();
        return true;
      }
      return false;
    },
  });

  const previousMonthScenePosition = getMonthScenePosition(previousMonth, -monthSceneTravel);
  const currentMonthScenePosition = getMonthScenePosition(currentMonth, 0);
  const nextMonthScenePosition = getMonthScenePosition(nextMonth, monthSceneTravel);

  const previousMonthSceneStyle = {
    transform: [
      {
        translateX: previousMonthScenePosition,
      },
    ],
  };

  const currentMonthSceneStyle = {
    transform: [
      {
        translateX: transitionMonth
          ? monthTransitionProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [0, -monthTransitionDirection * monthSceneTravel],
          })
          : currentMonthScenePosition,
      },
    ],
  };

  const incomingMonthSceneStyle = transitionMonth
    ? {
      transform: [
        {
          translateX: monthTransitionProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [monthTransitionDirection * monthSceneTravel, 0],
          }),
        },
      ],
    }
    : null;

  const nextMonthSceneStyle = {
    transform: [
      {
        translateX: nextMonthScenePosition,
      },
    ],
  };

  const getMonthTitleSceneOpacity = (position: Animated.Value) => position.interpolate({
    inputRange: [-monthSceneTravel, 0, monthSceneTravel],
    outputRange: [0, 1, 0],
    extrapolate: 'clamp',
  });
  const monthTitleTravelScale = 0.28;
  const previousMonthTitleSceneStyle = {
    opacity: getMonthTitleSceneOpacity(previousMonthScenePosition),
    transform: [{ translateX: Animated.multiply(previousMonthScenePosition, monthTitleTravelScale) }],
  };
  const currentMonthTitleSceneStyle = {
    opacity: transitionMonth
      ? monthTransitionProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 0],
      })
      : getMonthTitleSceneOpacity(currentMonthScenePosition),
    transform: [{
      translateX: transitionMonth
        ? monthTransitionProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -monthTransitionDirection * monthSceneTravel * monthTitleTravelScale],
        })
        : Animated.multiply(currentMonthScenePosition, monthTitleTravelScale),
    }],
  };
  const nextMonthTitleSceneStyle = {
    opacity: getMonthTitleSceneOpacity(nextMonthScenePosition),
    transform: [{ translateX: Animated.multiply(nextMonthScenePosition, monthTitleTravelScale) }],
  };
  const incomingMonthTitleSceneStyle = transitionMonth
    ? {
      opacity: monthTransitionProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
      }),
      transform: [{
        translateX: monthTransitionProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [monthTransitionDirection * monthSceneTravel * monthTitleTravelScale, 0],
        }),
      }],
    }
    : null;

  const dayPanelAnimatedStyle = {
    transform: [
      {
        translateY: dayPanelProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [dayPanelLift, 0],
        }),
      },
    ],
  };

  const renderMonthRows = useCallback((renderWeeks: LuxonDateTime[][], renderMonth: LuxonDateTime) => {
    const renderAvailableCellHeight = Math.max(
      56,
      Math.floor(
        (height - insets.top - bottomChrome - headerLayoutReserve)
        / Math.max(renderWeeks.length, 1),
      ),
    );
    const renderMaxPreviewRows = Math.max(
      3,
      Math.min(isCompactScreen ? 4 : 5, Math.floor((renderAvailableCellHeight - 30) / 17)),
    );
    const renderBaseSurfaceHeight = Math.max(renderAvailableCellHeight - 4, 0);

    return renderWeeks.map((week, weekIndex) => (
    <View
      key={`week-${renderMonth.toISODate()}-${weekIndex}`}
      style={[
        styles.weekRow,
        week.some((day) => day.hasSame(renderMonth, 'month') && toDateKey(day) === expandedDate) && styles.weekRowExpanded,
        { minHeight: renderAvailableCellHeight },
      ]}
    >
      {week.map((day, dayIndex) => {
        const dateKey = toDateKey(day);
        const dayEvents = eventsByDate.get(dateKey) ?? [];
        const isSelected = dateKey === selectedDate;
        const isExpanded = dateKey === expandedDate;
        const isToday = dateKey === todayKey;
        const isTodaySelected = isToday && isSelected;
        const inMonth = day.hasSame(renderMonth, 'month');
        const visibleEventCount = inMonth ? dayEvents.length : 0;
        const visibleEvents = inMonth ? dayEvents.slice(0, isExpanded ? visibleEventCount : renderMaxPreviewRows) : [];
        const isEmptyDay = visibleEventCount === 0;
        const hasCountBadge = visibleEventCount > 0;
        const remaining = Math.max(0, visibleEventCount - visibleEvents.length);
        const expandedVisibleEventCount = isExpanded ? visibleEvents.length : 0;
        const magnetMotionStyle = getMagnetMotionStyle(weekIndex, dayIndex, isExpanded);
        const expandedSurfaceHeight = isExpanded
          ? Math.max(
            renderBaseSurfaceHeight,
            6
            + expandedHeaderHeight
            + (expandedVisibleEventCount > 0
              ? expandedListTopPadding
                + (expandedVisibleEventCount * expandedEventHeight)
                + (Math.max(expandedVisibleEventCount - 1, 0) * expandedEventGap)
              : 0)
            + 4,
          )
          : renderBaseSurfaceHeight;
        const expandedSurfaceFrame = isExpanded
          ? {
            left: dayIndex === 0 ? 0 : dayIndex === 6 ? -(expandedSideBleed * 2) : -expandedSideBleed,
            right: dayIndex === 6 ? 0 : dayIndex === 0 ? -(expandedSideBleed * 2) : -expandedSideBleed,
            height: expandedSurfaceHeight + expandedBottomBleed,
            paddingBottom: expandedBottomBleed + 4,
          }
          : null;
        if (!inMonth) {
          return <View key={dateKey} style={styles.outsideMonthSpacer} />;
        }
        const dayCellContent = (
          <AnimatedPressable
            key={dateKey}
            onPress={() => handleDatePress(day)}
            onLongPress={() => handleDateLongPress(day)}
            delayLongPress={420}
            style={[styles.dayCell, magnetMotionStyle, isExpanded && styles.dayCellExpanded]}
            accessibilityRole="button"
            accessibilityLabel={day.setLocale(locale).toFormat('cccc, LLLL d')}
          >
            <View
              style={[
                styles.dayCellSurface,
                isEmptyDay && styles.dayCellSurfaceEmpty,
                isExpanded && styles.dayCellSurfaceExpanded,
                isExpanded && expandedSurfaceFrame,
                isToday && styles.dayCellToday,
                isSelected && styles.dayCellSelected,
                isTodaySelected && styles.dayCellTodaySelected,
                !isToday && !isSelected && { backgroundColor: getDensityBg(dayEvents.length, isSelected, densityTintColor) || baseDayCellSurfaceColor },
                isExpanded && styles.dayCellSurfaceOccluded,
                isExpanded && isToday && styles.dayCellSurfaceOccludedToday,
              ]}
            >
              <View style={[styles.dayHeaderRow, hasCountBadge && styles.dayHeaderRowWithCount, isEmptyDay && styles.dayHeaderRowEmpty]}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.dayNumber,
                    isEmptyDay && styles.dayNumberEmpty,
                    isToday && styles.dayNumberToday,
                    isToday && hasCountBadge && styles.dayNumberTodayCompact,
                    isSelected && styles.dayNumberSelected,
                    isTodaySelected && styles.dayNumberTodaySelected,
                    isTodaySelected && hasCountBadge && styles.dayNumberTodaySelectedCompact,
                  ]}
                >
                  {day.day}
                </Text>
                {hasCountBadge && (
                  <View
                    style={[
                      styles.countBadge,
                      isSelected && styles.countBadgeSelected,
                      isToday && styles.countBadgeToday,
                      isTodaySelected && styles.countBadgeTodaySelected,
                    ]}
                  >
                    <Text
                      style={[
                        styles.countBadgeText,
                        isSelected && styles.countBadgeTextSelected,
                        isToday && styles.countBadgeTextToday,
                        isTodaySelected && styles.countBadgeTextTodaySelected,
                      ]}
                    >
                      {visibleEventCount}
                    </Text>
                  </View>
                )}
              </View>
              {!isEmptyDay && (
                <View style={[styles.eventList, isExpanded && styles.eventListExpanded]}>
                  {visibleEvents.map((event) => (
                    isExpanded && !dayPanelOpen ? (
                      <Pressable
                        key={event.id}
                        onPress={(eventPress) => {
                          eventPress.stopPropagation?.();
                          openDayPanelForDate(day);
                        }}
                        onLongPress={(eventPress) => {
                          eventPress.stopPropagation?.();
                          handleQuickAdd(dateKey);
                        }}
                        delayLongPress={320}
                        style={[
                          styles.eventPill,
                          styles.eventPillInteractive,
                          { borderColor: event.color },
                          event.isDone && styles.eventDone,
                        ]}
                      >
                        <Text style={[styles.eventText, isSelected && styles.eventTextSelected, { color: event.color }, event.isDone && styles.eventTextDone]} numberOfLines={1}>{event.body}</Text>
                      </Pressable>
                    ) : (
                      <View
                        key={event.id}
                        style={[
                          styles.eventPill,
                          { borderColor: event.color },
                          event.isDone && styles.eventDone,
                        ]}
                      >
                        <Text style={[styles.eventText, isSelected && styles.eventTextSelected, { color: event.color }, event.isDone && styles.eventTextDone]} numberOfLines={1}>{event.body}</Text>
                      </View>
                    )
                  ))}
                  {isExpanded && remaining > 0 && (
                    <Text style={[styles.moreText, isSelected && styles.moreTextSelected]} numberOfLines={1}>+{remaining} more</Text>
                  )}
                </View>
              )}
            </View>
          </AnimatedPressable>
        );

        if (isExpanded && !dayPanelOpen) {
          const collapseGesture = Gesture.Pan()
            .activeOffsetY([-8, 8])
            .failOffsetX([-18, 18])
            .onEnd((event) => {
              const dy = event.translationY;
              const vy = event.velocityY;
              if (dy <= -CELL_COLLAPSE_SWIPE_DISTANCE || vy <= -CELL_COLLAPSE_SWIPE_VELOCITY) {
                runOnJS(collapseExpandedPreview)();
              }
            });

          return (
            <GestureDetector key={dateKey} gesture={collapseGesture}>
              {dayCellContent}
            </GestureDetector>
          );
        }

        return dayCellContent;
      })}
    </View>
    ));
  }, [
    baseDayCellSurfaceColor,
    bottomChrome,
    collapseExpandedPreview,
    dayPanelOpen,
    densityTintColor,
    eventsByDate,
    expandedBottomBleed,
    expandedDate,
    expandedEventGap,
    expandedEventHeight,
    expandedHeaderHeight,
    expandedListTopPadding,
    expandedSideBleed,
    getMagnetMotionStyle,
    handleDateLongPress,
    handleDatePress,
    handleQuickAdd,
    headerLayoutReserve,
    height,
    insets.top,
    isCompactScreen,
    locale,
    openDayPanelForDate,
    selectedDate,
    todayKey,
  ]);

  const monthRowsCacheRef = useRef<{
    renderer: typeof renderMonthRows;
    rows: Map<string, React.ReactNode>;
  } | null>(null);
  if (!monthRowsCacheRef.current || monthRowsCacheRef.current.renderer !== renderMonthRows) {
    monthRowsCacheRef.current = { renderer: renderMonthRows, rows: new Map() };
  }
  const monthRowsCache = monthRowsCacheRef.current.rows;
  const getCachedMonthRows = (month: LuxonDateTime, monthWeeks: LuxonDateTime[][]) => {
    const key = month.toFormat('yyyy-MM');
    if (!monthRowsCache.has(key)) {
      monthRowsCache.set(key, renderMonthRows(monthWeeks, month));
    }
    return monthRowsCache.get(key);
  };

  const deferredFarMonthDirection = deferredFarMonthDirectionRef.current;
  const renderPreviousMonthScene = preloadFarMonth || deferredFarMonthDirection === 1;
  const renderNextMonthScene = preloadFarMonth || deferredFarMonthDirection === -1;
  const previousMonthRows = renderPreviousMonthScene
    ? getCachedMonthRows(previousMonth, previousWeeks)
    : null;
  const currentMonthRows = getCachedMonthRows(currentMonth, weeks);
  const nextMonthRows = renderNextMonthScene
    ? getCachedMonthRows(nextMonth, nextWeeks)
    : null;
  const transitionMonthRows = transitionMonth && transitionWeeks
    ? getCachedMonthRows(transitionMonth, transitionWeeks)
    : null;

  return (
    <View style={styles.root}>
      <SafeAreaView style={[styles.screen, { paddingBottom: bottomChrome + 6 }]} edges={['top', 'left', 'right']}>
        <View style={styles.heroCard}>
          <View style={styles.heroOrbPrimary} />
          <View style={styles.heroOrbSecondary} />
          <BlurView intensity={60} tint="dark" style={StyleSheet.absoluteFillObject} />

          <View style={styles.heroContent}>
            <View style={styles.header}>
              <Pressable onPress={() => shiftMonth(-1)} style={styles.monthNavButton} accessibilityRole="button" accessibilityLabel={t('calendar.previousMonth', 'Previous month')}>
                <Ionicons name="chevron-back" size={18} color="#fff" />
              </Pressable>
              <View style={styles.monthTitleBlock}>
                {renderPreviousMonthScene && (
                  <Animated.View
                    key={`title-${previousMonth.toFormat('yyyy-MM')}`}
                    style={[styles.monthTitleLayer, styles.monthTitleLayerOverlay, previousMonthTitleSceneStyle]}
                    pointerEvents="none"
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                  >
                    <View style={styles.monthTitlePill}>
                      <Text style={styles.monthTitle} numberOfLines={1}>{previousMonthTitleLabel}</Text>
                      <Ionicons name="chevron-down" size={14} color="rgba(255,255,255,0.72)" />
                    </View>
                    {!isHeroCollapsed && <Text style={styles.monthSubtitle} numberOfLines={1}>{previousMonthSummaryLabel}</Text>}
                  </Animated.View>
                )}
                <Animated.View
                  key={`title-${currentMonth.toFormat('yyyy-MM')}`}
                  style={[styles.monthTitleLayer, currentMonthTitleSceneStyle]}
                >
                  <Pressable onPress={openMonthPicker} style={styles.monthTitlePill} accessibilityRole="button" accessibilityLabel={t('calendar.chooseMonth', 'Choose month')}>
                    <Text style={styles.monthTitle} numberOfLines={1}>{monthTitleLabel}</Text>
                    <Ionicons name="chevron-down" size={14} color="rgba(255,255,255,0.72)" />
                  </Pressable>
                  {!isHeroCollapsed && <Text style={styles.monthSubtitle} numberOfLines={1}>{monthSummaryLabel}</Text>}
                </Animated.View>
                {renderNextMonthScene && (
                  <Animated.View
                    key={`title-${nextMonth.toFormat('yyyy-MM')}`}
                    style={[styles.monthTitleLayer, styles.monthTitleLayerOverlay, nextMonthTitleSceneStyle]}
                    pointerEvents="none"
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                  >
                    <View style={styles.monthTitlePill}>
                      <Text style={styles.monthTitle} numberOfLines={1}>{nextMonthTitleLabel}</Text>
                      <Ionicons name="chevron-down" size={14} color="rgba(255,255,255,0.72)" />
                    </View>
                    {!isHeroCollapsed && <Text style={styles.monthSubtitle} numberOfLines={1}>{nextMonthSummaryLabel}</Text>}
                  </Animated.View>
                )}
                {transitionMonth && incomingMonthTitleSceneStyle && (
                  <Animated.View
                    key={`title-${transitionMonth.toFormat('yyyy-MM')}`}
                    style={[styles.monthTitleLayer, styles.monthTitleLayerOverlay, incomingMonthTitleSceneStyle]}
                    pointerEvents="none"
                  >
                    <View style={styles.monthTitlePill}>
                      <Text style={styles.monthTitle} numberOfLines={1}>{transitionMonthTitleLabel}</Text>
                      <Ionicons name="chevron-down" size={14} color="rgba(255,255,255,0.72)" />
                    </View>
                    {!isHeroCollapsed && <Text style={styles.monthSubtitle} numberOfLines={1}>{transitionMonthSummaryLabel}</Text>}
                  </Animated.View>
                )}
              </View>
              <Pressable onPress={() => shiftMonth(1)} style={styles.monthNavButton} accessibilityRole="button" accessibilityLabel={t('calendar.nextMonth', 'Next month')}>
                <Ionicons name="chevron-forward" size={18} color="#fff" />
              </Pressable>
            </View>

            {!isHeroCollapsed && allTags.length > 0 && (
              <View style={styles.headerTagLegend}>
                <TagLegend tags={allTags} tagColors={tagColors} onPressTag={toggleTagFilter} disabledTags={disabledTagFilters} variant="dark" />
              </View>
            )}

            {!isHeroCollapsed && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterHeader}>
                {localizedStatusFilters.map((filter) => {
                  const isOn = activeStatusFilters.has(filter.key);
                  const isDimmed = isStatusFilterActive && !isOn;
                  return (
                    <Pressable
                      key={filter.key}
                      onPress={() => toggleStatusFilter(filter.key)}
                      style={[styles.filterChip, isOn && isStatusFilterActive && styles.filterChipActive, isDimmed && styles.filterChipDimmed]}
                      accessibilityRole="button"
                      accessibilityLabel={t('calendar.filterLabel', '{{label}} filter', { label: filter.label })}
                    >
                      <Ionicons name={filter.icon} size={filterIconSize} color={filter.color} />
                      <Text style={[styles.filterLabel, isOn && isStatusFilterActive && styles.filterLabelActive, isDimmed && styles.filterLabelDimmed]} numberOfLines={1}>{filter.label}</Text>
                      <Text style={[styles.filterCount, isOn && isStatusFilterActive && styles.filterCountActive, isDimmed && styles.filterCountDimmed]}>{filterStats[filter.key]}</Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            )}

            <GestureDetector gesture={heroHandleGesture}>
              <Pressable
                onPress={toggleHero}
                accessibilityRole="button"
                accessibilityLabel={isHeroCollapsed ? t('calendar.expandHeader', 'Expand month header') : t('calendar.collapseHeader', 'Collapse month header')}
                accessibilityState={{ expanded: !isHeroCollapsed }}
                hitSlop={12}
                style={[styles.heroHandleHit, isHeroCollapsed && styles.heroHandleHitCollapsed]}
              >
                <View style={styles.heroHandleBar} />
              </Pressable>
            </GestureDetector>
          </View>
        </View>

        <View style={styles.weekdays}>
          {localizedWeekdayLabels.map((weekday) => <Text key={weekday} style={styles.weekday}>{weekday}</Text>)}
        </View>

        <GestureDetector gesture={monthSwipeGesture}>
          <View style={styles.monthGrid}>
            {renderPreviousMonthScene && (
              <Animated.View
                key={previousMonth.toFormat('yyyy-MM')}
                style={[styles.monthScene, styles.monthSceneOverlay, previousMonthSceneStyle]}
                pointerEvents="none"
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
              >
                {previousMonthRows}
              </Animated.View>
            )}
            <Animated.View
              key={currentMonth.toFormat('yyyy-MM')}
              style={[styles.monthScene, styles.monthSceneOverlay, currentMonthSceneStyle]}
              pointerEvents={transitionMonth ? 'none' : 'auto'}
            >
              {currentMonthRows}
            </Animated.View>
            {renderNextMonthScene && (
              <Animated.View
                key={nextMonth.toFormat('yyyy-MM')}
                style={[styles.monthScene, styles.monthSceneOverlay, nextMonthSceneStyle]}
                pointerEvents="none"
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
              >
                {nextMonthRows}
              </Animated.View>
            )}
            {transitionMonth && transitionMonthRows && (
              <Animated.View
                key={transitionMonth.toISODate() ?? 'transition-month'}
                style={[styles.monthScene, styles.monthSceneOverlay, incomingMonthSceneStyle]}
                pointerEvents="none"
              >
                {transitionMonthRows}
              </Animated.View>
            )}
          </View>
        </GestureDetector>
      </SafeAreaView>

      <Modal visible={monthPickerOpen} transparent animationType="fade" onRequestClose={closeMonthPicker}>
        <Pressable style={styles.monthPickerBackdrop} onPress={closeMonthPicker}>
          <Pressable style={styles.monthPickerCard} onPress={() => {}}>
            <View style={styles.monthPickerHeader}>
              <Pressable onPress={() => stepMonthPickerYear(-1)} style={styles.monthPickerYearButton} accessibilityRole="button" accessibilityLabel={t('calendar.previousYear', 'Previous year')}>
                <Ionicons name="chevron-back" size={16} color={colors.primary} />
              </Pressable>
              <Pressable
                onPress={toggleYearPicker}
                style={[styles.monthPickerYearToggle, yearPickerOpen && styles.monthPickerYearToggleActive]}
                accessibilityRole="button"
                accessibilityLabel={t('calendar.chooseYear', 'Choose year')}
                accessibilityState={{ expanded: yearPickerOpen }}
              >
                <Text style={styles.monthPickerYear}>{currentMonth.year}</Text>
                <Ionicons name={yearPickerOpen ? 'chevron-up' : 'chevron-down'} size={14} color={colors.primary} />
              </Pressable>
              <Pressable onPress={() => stepMonthPickerYear(1)} style={styles.monthPickerYearButton} accessibilityRole="button" accessibilityLabel={t('calendar.nextYear', 'Next year')}>
                <Ionicons name="chevron-forward" size={16} color={colors.primary} />
              </Pressable>
            </View>
            {yearPickerOpen ? (
              <View style={styles.yearPickerPanel}>
                <Text style={styles.yearPickerPanelLabel}>{t('calendar.chooseYear', 'Choose year')}</Text>
                <ScrollView
                  ref={yearPickerScrollRef}
                  style={styles.yearPickerScroll}
                  contentContainerStyle={styles.yearPickerScrollContent}
                  showsVerticalScrollIndicator={false}
                >
                  {yearPickerYears.map((year) => {
                    const isActiveYear = year === currentMonth.year;

                    return (
                      <Pressable
                        key={year}
                        onPress={() => handleSelectYearFromPicker(year)}
                        style={[styles.yearPickerOption, isActiveYear && styles.yearPickerOptionActive]}
                        accessibilityRole="button"
                        accessibilityLabel={t('calendar.selectYear', 'Select year {{year}}', { year })}
                      >
                        <Text style={[styles.yearPickerOptionText, isActiveYear && styles.yearPickerOptionTextActive]}>{year}</Text>
                        {isActiveYear ? <Ionicons name="checkmark-circle" size={18} color="#FFFFFF" /> : null}
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            ) : null}
            <View style={styles.monthPickerRowList}>
              {monthPickerRows.map((row, rowIndex) => (
                <View key={`month-row-${rowIndex}`} style={styles.monthPickerRow}>
                  {row.map((month) => {
                    const isActiveMonth = month.index === currentMonth.month;
                    const isCurrentMonth = currentMonth.year === DateTime.local().year && month.index === DateTime.local().month;

                    return (
                      <Pressable
                        key={month.index}
                        onPress={() => handleSelectMonthFromPicker(month.index)}
                        style={[
                          styles.monthPickerOption,
                          isCurrentMonth && !isActiveMonth && styles.monthPickerOptionCurrentMonth,
                          isActiveMonth && styles.monthPickerOptionActive,
                        ]}
                        accessibilityRole="button"
                        accessibilityLabel={t('calendar.selectMonth', 'Select {{month}}', { month: month.fullLabel })}
                      >
                        <Text
                          style={[
                            styles.monthPickerOptionText,
                            isCurrentMonth && !isActiveMonth && styles.monthPickerOptionTextCurrentMonth,
                            isActiveMonth && styles.monthPickerOptionTextActive,
                          ]}
                          numberOfLines={1}
                          minimumFontScale={0.9}
                        >
                          {month.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ))}
            </View>
            <Pressable
              onPress={() => {
                jumpToToday();
                setMonthPickerOpen(false);
              }}
              style={styles.monthPickerTodayShortcut}
              accessibilityRole="button"
              accessibilityLabel={t('calendar.goToToday', 'Go To Today')}
            >
              <Text style={styles.monthPickerTodayShortcutText}>{t('calendar.goToToday', 'Go To Today')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      {showPrototypeNav && (navOpen ? (
        <View style={[styles.expandedNav, { paddingBottom: insets.bottom > 0 ? insets.bottom : 8 }]}>
          <Pressable onPress={() => setNavOpen(false)} style={styles.navHandleButton} accessibilityRole="button" accessibilityLabel={t('common.hideNavigation', 'Hide navigation')}>
            <View style={styles.navHandle} />
          </Pressable>
          <PrototypeNavItems colors={colors} routerReplace={handlePrototypeNavRoutePress} />
        </View>
      ) : (
        <View style={[styles.collapsedNav, { paddingBottom: Math.max(insets.bottom, 6) }]}>
          <Pressable onPress={() => setNavOpen(true)} style={styles.collapsedNavOpener} accessibilityRole="button" accessibilityLabel={t('common.showNavigation', 'Show navigation')}>
            <Ionicons name="chevron-up" size={14} color={colors.primary} />
            <View style={styles.collapsedHandle} />
            <Ionicons name="calendar" size={14} color={colors.primary} />
          </Pressable>
          <View style={styles.collapsedTodaySlot}>
            <Pressable onPress={jumpToToday} style={styles.collapsedTodayButton} accessibilityRole="button" accessibilityLabel={t('calendar.today', 'Today')}>
              <Ionicons name="today-outline" size={12} color={colors.primary} />
              <Text style={styles.collapsedTodayText}>{t('calendar.today', 'Today')}</Text>
            </Pressable>
          </View>
        </View>
      ))}
      {renderDayPanel && (
        <Animated.View pointerEvents={dayPanelOpen ? 'auto' : 'none'} style={[styles.dayPanel, dayPanelAnimatedStyle, { top: dayPanelTop, bottom: bottomChrome }]}>
          <DayNavigatorSheet
            dayEvents={selectedCalendarEvents}
            allEvents={tagFilteredCalendarEvents}
            selectedDate={selectedDate}
            selectedWeekdayLabel={selectedWeekdayLabel}
            selectedDateDisplay={selectedDateDisplay}
            selectedDateLabel={selectedDateLabel}
            viewMode={viewMode}
            onChangeViewMode={setViewMode}
            onPressEvent={(event) => {
              if (event.seq) router.push({ pathname: '/reminder/[seq]', params: { seq: String(event.seq), mode: 'edit' } });
            }}
            onSelectDate={selectDateKey}
            onQuickAdd={handleQuickAdd}
            onResizeEvent={handleResizeEvent}
            onMoveEvent={handleMoveEvent}
            onChangeColumn={handleChangeColumn}
            onDragCreate={onDragCreate}
            onNavigateDay={navigateDay}
            onNavigateWeek={navigateWeek}
            onToggleSheet={() => setDayPanelOpen(false)}
            onRefresh={handleRefresh}
            refreshing={isRefreshing}
            tagColors={tagColors}
            bottomInset={0}
            headerGesture={headerGesture}
            daySwipeGesture={daySwipeGesture}
            headerAnimatedStyle={headerAnimatedStyle}
            contentAnimatedStyle={contentAnimatedStyle}
            eventsSwipeAnimatedStyle={eventsSwipeAnimatedStyle}
            dragToDismissEnabled
            isPresented={dayPanelOpen}
            fullScreen
          />
        </Animated.View>
      )}
    </View>
  );
}

function PrototypeNavItems({ colors, routerReplace }: { colors: ThemeColors; routerReplace: (route: string) => void }) {
  const items: Array<{ label: string; icon: TabIcon; active?: boolean; route: string }> = [
    { label: 'Chat', icon: 'chatbubble-outline', route: '/(tabs)/chat' },
    { label: 'Reminders', icon: 'notifications-outline', route: '/(tabs)/reminders' },
    { label: 'Calendar', icon: 'calendar', active: true, route: '/(tabs)/calendar' },
    { label: 'Settings', icon: 'settings-outline', route: '/(tabs)/settings' },
  ];

  return (
    <View style={navStyles.itemsRow}>
      {items.map((item) => {
        const color = item.active ? colors.tabBarActive : colors.tabBarInactive;
        return (
          <Pressable key={item.label} onPress={() => routerReplace(item.route)} style={navStyles.item} accessibilityRole="button" accessibilityLabel={item.label}>
            <Ionicons name={item.icon} size={22} color={color} />
            <Text style={[navStyles.label, { color }]} numberOfLines={1}>{item.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const navStyles = StyleSheet.create({
  itemsRow: {
    minHeight: 58,
    flexDirection: 'row',
    alignItems: 'center',
  },
  item: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  label: {
    fontSize: 11,
    lineHeight: 14,
    fontFamily: 'Inter_500Medium',
  },
});

const createStyles = (colors: ThemeColors, prototypePalette: PrototypePalette, { isDark, isNarrowScreen, isCompactScreen }: { isDark: boolean; isNarrowScreen: boolean; isCompactScreen: boolean }) => {
  const headerButtonSize = isCompactScreen ? 30 : isNarrowScreen ? 34 : 36;
  const headerGap = isCompactScreen ? 6 : 8;
  const heroHorizontal = isCompactScreen ? 12 : 14;
  const heroTop = isCompactScreen ? 8 : isNarrowScreen ? 10 : 12;
  const monthTitleSize = isCompactScreen ? 15 : isNarrowScreen ? 16 : 18;
  const monthTitleLineHeight = isCompactScreen ? 19 : isNarrowScreen ? 20 : 22;
  const monthSubtitleSize = isCompactScreen ? 9 : 11;
  const monthSubtitleLineHeight = isCompactScreen ? 11 : 14;
  const todayFontSize = isCompactScreen ? 11 : 12;
  const weekdayFontSize = isCompactScreen ? 9 : 10;
  const weekdayLineHeight = isCompactScreen ? 11 : 13;
  const filterFontSize = isCompactScreen ? 9 : isNarrowScreen ? 9 : 10;
  const filterLineHeight = isCompactScreen ? 11 : isNarrowScreen ? 11 : 12;
  const dayNumberSize = isCompactScreen ? 13 : 14;
  const dayNumberLineHeight = isCompactScreen ? 16 : 18;
  const eventTextSize = isCompactScreen ? 8.5 : 9;
  const eventTextLineHeight = isCompactScreen ? 10 : 11;

  return StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: prototypePalette.pageBackground,
  },
  screen: {
    flex: 1,
    paddingHorizontal: 8,
    backgroundColor: prototypePalette.pageBackground,
  },
  heroCard: {
    marginTop: 8,
    marginBottom: 6,
    borderRadius: 24,
    backgroundColor: prototypePalette.heroBackground,
    borderWidth: 1,
    borderColor: prototypePalette.heroBorder,
    overflow: 'hidden',
    shadowColor: prototypePalette.sheetShadow,
    shadowOpacity: isDark ? 0.32 : 0.2,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
    elevation: 6,
  },
  heroOrbPrimary: {
    position: 'absolute',
    width: 240,
    height: 240,
    borderRadius: 120,
    backgroundColor: prototypePalette.heroPrimaryOrb,
    top: -80,
    right: -60,
  },
  heroOrbSecondary: {
    position: 'absolute',
    width: 180,
    height: 180,
    borderRadius: 90,
    backgroundColor: prototypePalette.heroSecondaryOrb,
    bottom: -60,
    left: -40,
  },
  heroContent: {
    zIndex: 1,
    paddingTop: heroTop,
    paddingHorizontal: heroHorizontal,
  },
  heroTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  heroCopy: {
    flex: 1,
    minWidth: 0,
    paddingRight: 8,
  },
  heroTitle: {
    fontSize: 24,
    lineHeight: 30,
    fontFamily: 'Inter_400Regular',
    color: '#fff',
    letterSpacing: 0.5,
  },
  heroDescription: {
    marginTop: 4,
    color: prototypePalette.heroSubtext,
    fontSize: 11,
    lineHeight: 14,
    fontFamily: 'Inter_500Medium',
  },
  header: {
    minHeight: isCompactScreen ? 38 : isNarrowScreen ? 42 : 46,
    flexDirection: 'row',
    alignItems: 'center',
    gap: headerGap,
    marginTop: 0,
  },
  iconButton: {
    width: headerButtonSize,
    height: headerButtonSize,
    borderRadius: headerButtonSize / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: prototypePalette.heroButtonBg,
    borderWidth: 0.5,
    borderColor: prototypePalette.heroButtonBorder,
  },
  monthNavButton: {
    width: headerButtonSize,
    height: headerButtonSize,
    borderRadius: headerButtonSize / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: prototypePalette.heroButtonBg,
    borderWidth: 0.5,
    borderColor: prototypePalette.heroButtonBorder,
  },
  monthTitleBlock: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    paddingHorizontal: isCompactScreen ? 1 : 0,
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  monthTitleLayer: {
    width: '100%',
    alignItems: 'center',
  },
  monthTitleLayerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
  },
  monthTitlePill: {
    maxWidth: '100%',
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isCompactScreen ? 3 : 4,
    paddingHorizontal: isCompactScreen ? 10 : 14,
    paddingVertical: isCompactScreen ? 6 : 8,
    borderRadius: 999,
    backgroundColor: prototypePalette.heroButtonBg,
    borderWidth: 0.5,
    borderColor: prototypePalette.heroButtonBorder,
  },
  monthTitle: {
    color: prototypePalette.monthTitle,
    fontSize: monthTitleSize,
    lineHeight: monthTitleLineHeight,
    fontFamily: 'PlusJakartaSans_700Bold',
  },
  monthSubtitle: {
    marginTop: isCompactScreen ? 3 : 4,
    color: prototypePalette.heroSubtext,
    fontSize: monthSubtitleSize,
    lineHeight: monthSubtitleLineHeight,
    fontFamily: 'Inter_500Medium',
    textAlign: 'center',
  },
  monthPickerBackdrop: {
    flex: 1,
    backgroundColor: isDark ? 'rgba(2, 9, 16, 0.62)' : 'rgba(12, 27, 45, 0.28)',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  monthPickerCard: {
    borderRadius: 20,
    padding: 18,
    backgroundColor: prototypePalette.dayPanelBackground,
    borderWidth: 1,
    borderColor: prototypePalette.dayPanelBorder,
    shadowColor: prototypePalette.sheetShadow,
    shadowOpacity: isDark ? 0.32 : 0.14,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
    elevation: 12,
  },
  monthPickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
  },
  monthPickerYearButton: {
    minWidth: 40,
    minHeight: 40,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: prototypePalette.todayButtonBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.todayButtonBorder,
  },
  monthPickerYearToggle: {
    minHeight: 42,
    minWidth: 108,
    paddingHorizontal: 14,
    borderRadius: 999,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: prototypePalette.dayCellSurface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.dayCellBorder,
  },
  monthPickerYearToggleActive: {
    backgroundColor: prototypePalette.todayButtonBg,
    borderColor: prototypePalette.todayButtonBorder,
  },
  monthPickerYear: {
    color: prototypePalette.eventText,
    fontSize: 20,
    lineHeight: 24,
    fontFamily: 'PlusJakartaSans_700Bold',
  },
  yearPickerPanel: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 18,
    backgroundColor: prototypePalette.dayCellSurface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.dayCellBorder,
  },
  yearPickerPanelLabel: {
    marginBottom: 8,
    paddingHorizontal: 4,
    color: prototypePalette.moreText,
    fontSize: 10,
    lineHeight: 12,
    fontFamily: 'Inter_700Bold',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  yearPickerScroll: {
    maxHeight: isCompactScreen ? 180 : 220,
  },
  yearPickerScrollContent: {
    gap: 8,
    paddingBottom: 4,
  },
  yearPickerOption: {
    minHeight: YEAR_PICKER_ITEM_HEIGHT,
    paddingHorizontal: 14,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: prototypePalette.dayCellSurfaceRaised,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.dayCellBorder,
  },
  yearPickerOptionActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  yearPickerOptionText: {
    color: prototypePalette.eventText,
    fontSize: 15,
    lineHeight: 18,
    fontFamily: 'PlusJakartaSans_700Bold',
  },
  yearPickerOptionTextActive: {
    color: '#FFFFFF',
  },
  monthPickerRowList: {
    gap: 10,
  },
  monthPickerRow: {
    flexDirection: 'row',
    gap: 8,
  },
  monthPickerOption: {
    flex: 1,
    minWidth: 0,
    minHeight: isCompactScreen ? 44 : 48,
    paddingHorizontal: isCompactScreen ? 4 : 6,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: prototypePalette.dayCellSurfaceRaised,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.dayCellBorder,
  },
  monthPickerOptionActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  monthPickerOptionCurrentMonth: {
    backgroundColor: prototypePalette.todayNumberBg,
    borderColor: prototypePalette.todaySelectedBorder,
    shadowColor: TODAY_ACCENT,
    shadowOpacity: isDark ? 0.16 : 0.08,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  monthPickerOptionText: {
    color: prototypePalette.eventText,
    fontSize: isCompactScreen ? 10 : 11,
    lineHeight: isCompactScreen ? 13 : 14,
    fontFamily: 'Inter_600SemiBold',
    textAlign: 'center',
  },
  monthPickerOptionTextActive: {
    color: '#FFFFFF',
  },
  monthPickerOptionTextCurrentMonth: {
    color: prototypePalette.todayNumberText,
  },
  monthPickerTodayShortcut: {
    alignSelf: 'flex-start',
    marginTop: 14,
    minHeight: 34,
    paddingHorizontal: 12,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: prototypePalette.todayButtonBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.todayButtonBorder,
  },
  monthPickerTodayShortcutText: {
    color: colors.primary,
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Inter_700Bold',
  },
  todayButton: {
    minHeight: headerButtonSize,
    paddingHorizontal: isCompactScreen ? 10 : 14,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderWidth: 0.5,
    borderColor: 'rgba(255,255,255,0.28)',
    flexShrink: 0,
  },
  todayButtonText: {
    color: '#fff',
    fontSize: todayFontSize,
    fontFamily: 'Inter_700Bold',
  },
  headerTagLegend: {
    marginTop: isCompactScreen ? 3 : 4,
  },
  weekdays: {
    height: isCompactScreen ? 20 : 24,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 2,
  },
  weekday: {
    flex: 1,
    textAlign: 'center',
    color: prototypePalette.weekday,
    fontSize: weekdayFontSize,
    lineHeight: weekdayLineHeight,
    fontFamily: 'Inter_700Bold',
    textTransform: 'uppercase',
  },
  filterHeader: {
    minHeight: isCompactScreen ? 32 : isNarrowScreen ? 38 : 42,
    flexDirection: 'row',
    alignItems: 'center',
    gap: isCompactScreen ? 5 : isNarrowScreen ? 6 : 8,
    paddingTop: isCompactScreen ? 3 : isNarrowScreen ? 4 : 6,
    paddingBottom: isCompactScreen ? 1 : isNarrowScreen ? 3 : 4,
  },
  filterChip: {
    minWidth: 0,
    minHeight: isCompactScreen ? 28 : isNarrowScreen ? 31 : 34,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: prototypePalette.filterBorder,
    backgroundColor: prototypePalette.filterBg,
    paddingHorizontal: isCompactScreen ? 7 : isNarrowScreen ? 8 : 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isCompactScreen ? 3 : isNarrowScreen ? 3 : 4,
  },
  filterChipActive: {
    borderColor: prototypePalette.filterBorderStrong,
    backgroundColor: prototypePalette.filterBgActive,
  },
  filterChipDimmed: {
    opacity: 0.42,
  },
  filterLabel: {
    minWidth: 0,
    flexShrink: 1,
    color: 'rgba(255,255,255,0.68)',
    fontSize: filterFontSize,
    lineHeight: filterLineHeight,
    fontFamily: 'Inter_700Bold',
  },
  filterLabelActive: {
    color: '#fff',
  },
  filterLabelDimmed: {
    color: 'rgba(255,255,255,0.35)',
  },
  filterCount: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: filterFontSize,
    lineHeight: filterLineHeight,
    fontFamily: 'Inter_700Bold',
  },
  filterCountActive: {
    color: '#fff',
  },
  filterCountDimmed: {
    color: 'rgba(255,255,255,0.35)',
  },
  monthGrid: {
    flex: 1,
    gap: 0,
    overflow: 'visible',
    position: 'relative',
  },
  monthScene: {
    flex: 1,
    overflow: 'visible',
  },
  monthSceneOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  weekRow: {
    flex: 1,
    flexDirection: 'row',
    gap: 0,
    borderRadius: 20,
    position: 'relative',
    overflow: 'visible',
  },
  weekRowExpanded: {
    zIndex: 20,
  },
  dayCell: {
    flex: 1,
    minWidth: 0,
    borderRadius: 24,
    padding: 2,
    overflow: 'hidden',
  },
  dayCellExpanded: {
    zIndex: 12,
    overflow: 'visible',
  },
  outsideMonthSpacer: {
    flex: 1,
    minWidth: 0,
    padding: 2,
  },
  dayCellSurface: {
    flex: 1,
    borderRadius: 24,
    backgroundColor: prototypePalette.dayCellSurface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.dayCellBorder,
    paddingTop: 6,
    paddingHorizontal: 3,
    paddingBottom: 4,
    overflow: 'hidden',
  },
  dayCellSurfaceEmpty: {
    paddingTop: 6,
    paddingBottom: 4,
    paddingHorizontal: 0,
  },
  dayCellSurfaceExpanded: {
    position: 'absolute',
    top: 0,
    zIndex: 1,
    borderColor: prototypePalette.dayCellBorderStrong,
    shadowColor: prototypePalette.sheetShadow,
    shadowOpacity: isDark ? 0.18 : 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 2,
  },
  dayCellSurfaceOccluded: {
    backgroundColor: prototypePalette.dayCellSurfaceRaised,
  },
  dayCellSurfaceOccludedToday: {
    backgroundColor: prototypePalette.todayExpandedSurface,
  },
  dayCellToday: {
    borderColor: prototypePalette.todayBorder,
    backgroundColor: prototypePalette.todaySurface,
  },
  dayCellSelected: {
    borderColor: prototypePalette.selectedBorder,
    backgroundColor: prototypePalette.selectedSurface,
    borderRadius: 21,
    borderWidth: 1,
  },
  dayCellTodaySelected: {
    borderColor: prototypePalette.todaySelectedBorder,
    backgroundColor: prototypePalette.todaySelectedSurface,
    shadowColor: TODAY_ACCENT,
    shadowOpacity: isDark ? 0.2 : 0.14,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  dayHeaderRow: {
    minHeight: isCompactScreen ? 18 : 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 3,
    paddingHorizontal: 2,
  },
  dayHeaderRowWithCount: {
    paddingRight: isCompactScreen ? 16 : 18,
  },
  dayHeaderRowEmpty: {
    minHeight: 0,
    justifyContent: 'center',
    paddingHorizontal: 0,
  },
  dayNumber: {
    color: prototypePalette.eventText,
    fontSize: dayNumberSize,
    lineHeight: dayNumberLineHeight,
    fontFamily: 'Inter_500Medium',
    minWidth: 20,
  },
  dayNumberEmpty: {
    minWidth: 0,
    textAlign: 'center',
  },
  dayNumberToday: {
    color: prototypePalette.todayNumberText,
    fontFamily: 'Inter_700Bold',
    minWidth: 24,
    paddingHorizontal: 6,
    borderRadius: 999,
    textAlign: 'center',
    backgroundColor: prototypePalette.todayNumberBg,
  },
  dayNumberTodayCompact: {
    fontSize: isCompactScreen ? 11 : 12,
    lineHeight: isCompactScreen ? 13 : 15,
    minWidth: isCompactScreen ? 13 : 15,
    flexShrink: 0,
    paddingHorizontal: 0,
    textAlign: 'center',
    backgroundColor: 'transparent',
  },
  dayNumberSelected: {
    color: prototypePalette.selectedText,
    fontFamily: 'Inter_700Bold',
  },
  dayNumberTodaySelected: {
    color: '#FFFFFF',
    backgroundColor: TODAY_ACCENT,
  },
  dayNumberTodaySelectedCompact: {
    color: prototypePalette.todayNumberText,
    backgroundColor: 'transparent',
  },
  countBadge: {
    position: 'absolute',
    top: 2,
    right: 2,
    minWidth: isCompactScreen ? 14 : 15,
    height: isCompactScreen ? 12 : 13,
    paddingHorizontal: 2,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: prototypePalette.countBadgeBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.countBadgeBorder,
  },
  countBadgeSelected: {
    backgroundColor: prototypePalette.countBadgeSelectedBg,
    borderColor: prototypePalette.countBadgeSelectedBorder,
  },
  countBadgeToday: {
    backgroundColor: prototypePalette.countBadgeTodayBg,
    borderColor: prototypePalette.countBadgeTodayBorder,
  },
  countBadgeTodaySelected: {
    backgroundColor: prototypePalette.countBadgeTodaySelectedBg,
    borderColor: prototypePalette.countBadgeTodaySelectedBorder,
  },
  countBadgeText: {
    color: prototypePalette.countText,
    fontSize: isCompactScreen ? 7 : 8,
    lineHeight: isCompactScreen ? 8 : 9,
    fontFamily: 'Inter_700Bold',
  },
  countBadgeTextSelected: {
    color: prototypePalette.countTextSelected,
  },
  countBadgeTextToday: {
    color: prototypePalette.countTextToday,
  },
  countBadgeTextTodaySelected: {
    color: prototypePalette.countTextTodaySelected,
  },
  eventList: {
    flex: 1,
    minHeight: 0,
    gap: 1,
    paddingTop: isCompactScreen ? 1 : 2,
  },
  eventListExpanded: {
    flexGrow: 0,
    flexShrink: 0,
  },
  eventPill: {
    minHeight: isCompactScreen ? 15 : 16,
    borderRadius: 7,
    paddingHorizontal: isCompactScreen ? 2 : 3,
    paddingVertical: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  eventPillInteractive: {
    cursor: 'pointer',
  },
  eventDone: {
    opacity: 0.92,
  },
  eventText: {
    flex: 1,
    minWidth: 0,
    color: prototypePalette.eventText,
    fontSize: eventTextSize,
    lineHeight: eventTextLineHeight,
    fontFamily: 'Inter_500Medium',
  },
  eventTextSelected: {
    color: prototypePalette.eventText,
  },
  eventTextDone: {
    textDecorationLine: 'line-through',
    opacity: 0.82,
  },
  moreText: {
    color: prototypePalette.moreText,
    fontSize: eventTextSize,
    lineHeight: eventTextLineHeight,
    fontFamily: 'Inter_700Bold',
    paddingHorizontal: 2,
    paddingTop: 0,
  },
  moreTextSelected: {
    color: colors.primary,
  },
  heroHandleHit: {
    width: '100%',
    paddingTop: isCompactScreen ? 4 : 6,
    paddingBottom: isCompactScreen ? 3 : 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroHandleHitCollapsed: {
    paddingTop: isCompactScreen ? 2 : 3,
  },
  heroHandleBar: {
    width: 44,
    height: 4,
    borderRadius: 2,
    backgroundColor: prototypePalette.handle,
  },
  collapsedNav: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: 34,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 10,
    backgroundColor: prototypePalette.navBackground,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: prototypePalette.navBorder,
    shadowColor: prototypePalette.sheetShadow,
    shadowOpacity: isDark ? 0.2 : 0.08,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: -4 },
    elevation: 8,
  },
  collapsedNavOpener: {
    minHeight: 28,
    paddingHorizontal: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },
  collapsedTodaySlot: {
    position: 'absolute',
    top: 0,
    right: 10,
    bottom: 0,
    justifyContent: 'center',
  },
  collapsedTodayButton: {
    minHeight: 26,
    paddingHorizontal: 10,
    borderRadius: 999,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: prototypePalette.todayButtonBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: prototypePalette.todayButtonBorder,
  },
  collapsedTodayText: {
    color: prototypePalette.selectedText,
    fontSize: 10,
    lineHeight: 12,
    fontFamily: 'Inter_700Bold',
  },
  collapsedHandle: {
    width: 44,
    height: 5,
    borderRadius: 999,
    backgroundColor: prototypePalette.navHandle,
  },
  expandedNav: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: 82,
    backgroundColor: prototypePalette.navBackground,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: prototypePalette.navBorder,
    shadowColor: prototypePalette.sheetShadow,
    shadowOpacity: isDark ? 0.22 : 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: -4 },
    elevation: 10,
  },
  navHandleButton: {
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navHandle: {
    width: 42,
    height: 4,
    borderRadius: 999,
    backgroundColor: prototypePalette.navHandle,
  },
  dayPanel: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 30,
  },
  });
};
